/**
 * Simple Live Price Overlay System
 *
 * This system creates in-cell overlays for live price updates
 * Overlays appear on top of static formula values
 */

import {DisposableWithEvents} from 'app/common/DisposableWithEvents';
import {LiveOverlayComm, PriceUpdate as OverlayPriceUpdate} from 'app/client/models/LiveOverlayComm';

// Disable debug logs: only console.error will output
if (typeof console !== 'undefined') {
  console.log = () => {};
  console.warn = () => {};
}

interface PriceUpdate {
  symbol: string;
  exchange: string;
  price: number;
  change: number;
  changePercent: number;
  timestamp: number;
  lastPrice?: number;
  latency?: {
    wsReceivedTime?: number;
    publishTime?: number;
    rpcReceivedTime?: number;
    clientSendTime?: number;
    clientReceivedTime?: number;
    clientLatency?: number;
    endToEndLatency?: number;
    totalLatency?: number;
    symbol?: string;
    exchange?: string;
  };
}

interface GenericFieldUpdate {
  symbol: string;
  exchange: string;
  field: string;   // e.g. 'volume', 'best_bid_price', 'order_status'
  value: number | string;  // Allow both numeric and string values (for order fields)
  timestamp: number;
  latency?: any;
}

// Buffered-hot-zone overlays are always enabled

export class SimpleLiveOverlay extends DisposableWithEvents {
  private _overlayComm = LiveOverlayComm.instance();
  private _subscribedSymbols = new Set<string>();
  private _priceCache = new Map<string, PriceUpdate>();
  

  
  // Lightweight observer for new formulas only
  private _observer: MutationObserver;
  private _isInitialized = false;
  // Map overlay element -> symbol key for quick cleanup
  private _overlayMap = new Map<HTMLElement, string>();
  // Track active hot symbols (symbol uppercase) and their reference count
  private _symbolRefCount = new Map<string, number>();
  // Keys (symbol-exchange) currently being snapshot-fetched
  private _pendingFetch = new Set<string>();
  private _hotZoneScheduled = false;
  // Reverse map: symbol -> Set of overlay elements for fast lookups
  private _symbolToOverlays = new Map<string, Set<HTMLElement>>();

  constructor() {
    super();
    console.log('🚀 SimpleLiveOverlay: Initializing instant overlay system...');

    // Initialize overlay system immediately
    this._injectGlobalStyles();
    this._injectTSLStatusStyles();
    this._initializeInstantOverlays();
    
    // Listen for overlay updates via Comm
    this._overlayComm.on('update', (update: OverlayPriceUpdate | GenericFieldUpdate) => {
      // Distinguish by presence of field property
      if ((update as any).field) {
        this._handleGenericFieldUpdate(update as GenericFieldUpdate);
      } else {
        this._handlePriceUpdate(update as OverlayPriceUpdate as unknown as PriceUpdate);
      }
    });

    // Set up lightweight observer for new formulas
    this._setupLightweightObserver();
    this._setupScrollListener();
    this._startRetryTimer();
    // Set up DOM rebuild detection for when Grist recreates table content
    this._setupDOMRebuildDetection();

    // Ensure hot zone scan covers restored scroll position
    requestAnimationFrame(() => this._updateHotZone());
    setTimeout(() => this._updateHotZone(), 300);

    console.log('✅ SimpleLiveOverlay: Instant initialization complete');
  }

  private _initializeInstantOverlays(): void {
    const allCells = document.querySelectorAll('.field_clip');
    const foundSymbols = new Set<string>();
    
    allCells.forEach((cell) => {
      const cellContent = cell.textContent || '';
      const formulaInfo = this._extractFormulaInfo(cellContent);

      if (formulaInfo) {
        const {symbol, exchange, field} = formulaInfo;
        const symbolKey = `${symbol}-${exchange}`;

        this._createDirectOverlay(cell as HTMLElement, symbol, exchange, field);

        // Stash LIVE_PNL args on the cell so the tick handler can compute
        // (ltp - avg) * qty without re-parsing the formula on every update.
        if (field === 'pnl_realtime') {
          const avg = (formulaInfo as any).avgPrice;
          const qty = (formulaInfo as any).quantity;
          if (avg !== undefined) (cell as HTMLElement).setAttribute('data-pnl-avg', String(avg));
          if (qty !== undefined) (cell as HTMLElement).setAttribute('data-pnl-qty', String(qty));
        }

        // Use EXCHANGE:SYMBOL format so the server subscribes depth for
        // exactly the exchange the formula references (not both).
        const exchangeTaggedSymbol = `${exchange.toUpperCase()}:${symbol.toUpperCase()}`;
        this._subscribedSymbols.add(exchangeTaggedSymbol);
        foundSymbols.add(exchangeTaggedSymbol);
        
        const cachedPrice = this._priceCache.get(symbolKey);
        if (cachedPrice && field === 'ltp') {
            this._updateCellOverlayDirect(cell as HTMLElement, cachedPrice);
        }
        // Also restore cached order field data immediately
        if (cachedPrice && field.startsWith('order_') && (cachedPrice as any)[field] !== undefined) {
          this._updateCellsForField(symbol, exchange, field, (cachedPrice as any)[field]);
        }
      }
    });
    
    // Ensure DOM updates and overlay registration complete before subscribing
    if (foundSymbols.size > 0) {
      // Use setTimeout to ensure overlays are fully registered in the DOM
      setTimeout(() => {
        console.log(`🔥 Subscribing to ${foundSymbols.size} symbols after overlay creation`);
        this._overlayComm.subscribe(Array.from(foundSymbols));
      }, 50); // Small delay to ensure DOM updates complete
    }
    
    this._isInitialized = true;

    // Depth sync handled server-side via DEPTH_SUBSCRIBE() formula in Python
  }

  private _createDirectOverlay(cell: HTMLElement, symbol: string, exchange: string, field: string = 'ltp'): void {
    // CRITICAL FIX: Check if overlay already exists to prevent duplicates
    const existingOverlay = cell.querySelector('.live-price-overlay-container');
    if (existingOverlay) {
      console.log(`⏭️ Skipping duplicate overlay creation for ${symbol}-${exchange}-${field}`);
      return;
    }
    
    // Mark cell as having overlay and set metadata
    cell.setAttribute('data-has-overlay', 'true');
    cell.setAttribute('data-symbol', symbol.toUpperCase());
    cell.setAttribute('data-exchange', exchange.toUpperCase());
    cell.setAttribute('data-field', field);
    
    // Create overlay element
    const overlay = document.createElement('div');
    overlay.className = 'live-price-overlay-container loading';
    overlay.setAttribute('data-symbol', symbol.toUpperCase());
    overlay.setAttribute('data-exchange', exchange.toUpperCase());
    overlay.setAttribute('data-field', field);
    
    // Create value display element
    const valueDisplay = document.createElement('div');
    valueDisplay.className = 'live-price-value';
    valueDisplay.innerHTML = '<div class="loader"></div>';
    overlay.appendChild(valueDisplay);
    
    // Create latency display element  
    const latencyDisplay = document.createElement('div');
    latencyDisplay.className = 'live-price-latency';
    latencyDisplay.textContent = '';
    overlay.appendChild(latencyDisplay);
    
    // Always show overlay, but may show loading state initially
    const keyCheck = `${symbol.toUpperCase()}-${exchange.toUpperCase()}`;
    const cachedData = this._priceCache.get(keyCheck);
    
    if (!cachedData) {
      // Show loading state but keep overlay visible
      overlay.style.visibility = 'visible';
      if (!this._pendingFetch.has(keyCheck)) {
        this._pendingFetch.add(keyCheck);
        console.log(`🚀 Immediately fetching data for new overlay: symbol="${symbol}" field="${field}" exchange="${exchange}"`);
        // Fetch price data; order fields will be populated via WebSocket subscription
        this._fetchSinglePrice(symbol.toUpperCase(), exchange.toUpperCase());
      }
    } else {
      // Show overlay with cached data immediately
      overlay.style.visibility = 'visible';
             // Apply cached data based on field type
       if (field === 'ltp') {
         this._updateCellOverlayDirect(cell, cachedData);
       } else if ((cachedData as any)[field] !== undefined) {
         console.log(`🔄 Applying cached ${field} data: ${symbol}=${(cachedData as any)[field]}`);
         this._updateCellsForField(symbol, exchange, field, (cachedData as any)[field]);
       } else if (field.startsWith('order_')) {
         console.log(`⚠️ No cached ${field} data for ${symbol}, will wait for WebSocket data`);
       }
    }
    
    // Insert overlay at the beginning of the cell
    cell.insertBefore(overlay, cell.firstChild);
    
    // Track overlay for cleanup
    const symbolUpper = symbol.toUpperCase();
    const key = `${symbolUpper}-${exchange.toUpperCase()}`;
    this._overlayMap.set(overlay, key);

    // Add to reverse symbol lookup
    if (!this._symbolToOverlays.has(symbolUpper)) {
      this._symbolToOverlays.set(symbolUpper, new Set());
    }
    this._symbolToOverlays.get(symbolUpper)!.add(overlay);

    // Manage symbol ref counts and subscriptions — keyed by EXCHANGE:SYMBOL
    // so the server subscribes depth for exactly the right exchange.
    const exchangeTag = `${exchange.toUpperCase()}:${symbolUpper}`;
    const prevCount = this._symbolRefCount.get(exchangeTag) || 0;
    this._symbolRefCount.set(exchangeTag, prevCount + 1);
    if (prevCount === 0) {
      this._overlayComm.subscribe([exchangeTag]);
      // If we do not yet have a cached price, trigger fetch
      if (!this._priceCache.has(key)) {
        this._fetchSinglePrice(symbolUpper, exchange);
      }
    }
  }

  private _setupLightweightObserver(): void {
    // Lightweight observer that only watches for new LIVE_* formulas
    this._observer = new MutationObserver((mutations) => {
      if (!this._isInitialized) return;

      let hasNewFormulas = false;
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              const text = element.textContent || '';
              if (text.includes('LIVE_')) {
                hasNewFormulas = true;
              }
            }
          });
        }
      });

      if (hasNewFormulas) {
        // Quick re-scan for new formulas only
        setTimeout(() => {
          this._scanForNewFormulas();
          this._cleanupOrphanedOverlays();
        }, 100);
      } else {
        // Even when no new formulas, check for orphaned overlays due to scroll/column move
        this._cleanupOrphanedOverlays();
      }

      // Depth sync handled by DEPTH_SUBSCRIBE() Python formula
    });
    
    const gridContainer = document.querySelector('.gridview_data_area') || document.body;
    this._observer.observe(gridContainer, {
      childList: true,
      subtree: true
    });
  }

  private _scanForNewFormulas(): void {
    // Only scan cells that don't already have overlays
    const newCells = document.querySelectorAll('.field_clip:not([data-has-overlay])');
    let newCount = 0;
    const newSymbols = new Set<string>();
    
    console.log(`📊 SimpleLiveOverlay: Scanning ${newCells.length} cells for formulas`);
    
    // Debug: Track all ORDER functions found
    const orderFunctionsFound: string[] = [];
    
    newCells.forEach((cell) => {
      const cellElement = cell as HTMLElement;
      const textAttr = cellElement.getAttribute('data-formula') || '';
      const text = textAttr || (cellElement.textContent || '');
      
      // Debug formula detection - log all ORDER functions found
      if (text.includes('ORDER_') || text.includes('FILLED_QTY') || text.includes('AVG_PRICE') || text.includes('TRIGGER_PRICE') || text.includes('LAST_FILL_TIME')) {
        console.log(`🔍 Found ORDER formula cell: "${text}"`);
        orderFunctionsFound.push(text);
      }
      
      const formulaInfo = this._extractFormulaInfo(text);
      
      if (formulaInfo) {
        const {symbol, exchange, field} = formulaInfo;
        const symbolKey = `${symbol}-${exchange}`;
        
        console.log(`✅ Detected formula: ${field} for ${symbol} on ${exchange}`);
        
        this._createDirectOverlay(cellElement, symbol, exchange, field);
        
        const exchangeTaggedSymbol = `${exchange.toUpperCase()}:${symbol.toUpperCase()}`;
        if (!this._subscribedSymbols.has(exchangeTaggedSymbol)) {
          this._subscribedSymbols.add(exchangeTaggedSymbol);
          newSymbols.add(exchangeTaggedSymbol);
          this._overlayComm.subscribe([exchangeTaggedSymbol]);
        }
        
        // Check for cached data and apply immediately
        const cachedPrice = this._priceCache.get(symbolKey);
        if (cachedPrice && field === 'ltp') {
          console.log(`🔄 Applying cached price to new cell ${symbol}: ${cachedPrice.price}`);
          this._updateCellOverlayDirect(cellElement, cachedPrice);
        }
        
        newCount++;
      }
    });
    
    // Enhanced debugging for ORDER functions
    if (orderFunctionsFound.length > 0) {
      console.log(`📋 DEBUG: Found ${orderFunctionsFound.length} ORDER functions:`, orderFunctionsFound);
      
      // Extract symbols from ORDER functions for debugging  
      const orderSymbols = orderFunctionsFound.map(formula => {
        const match = formula.match(/ORDER_\w+\s*\(\s*["']([^"']+)["']\s*\)/);
        return match ? match[1] : 'NO_SYMBOL_EXTRACTED';
      });
      console.log(`📋 DEBUG: Extracted ORDER symbols:`, orderSymbols);
      
      // Log what symbols would be requested
      console.log(`📋 DEBUG: Unique ORDER symbols to fetch:`, [...new Set(orderSymbols)]);
    }
    
    if (newCount > 0) {
      console.log(`📍 Added ${newCount} new overlays`);
      console.log(`📊 New symbols:`, Array.from(newSymbols));
      
      // Fetch prices for new symbols
      if (newSymbols.size > 0) {
        setTimeout(() => this._fetchInitialPrices(), 100);
      }
    }
  }

  private _injectGlobalStyles(): void {
    const style = document.createElement('style');
    style.id = 'simple-live-overlay-styles';
    style.textContent = `
      /* Ensure field_clip can contain positioned overlays while respecting text boundaries */
      .field_clip[data-has-overlay="true"] {
        position: relative !important;
        overflow: hidden !important;
      }
      
      /* Live price overlay styling - positioned within cell */
      .live-price-overlay-container {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        pointer-events: none !important;
        border-radius: 2px !important;
        z-index: 50 !important;
        transition: all 0.2s ease-out !important;
        will-change: opacity, transform !important;
        backface-visibility: hidden !important;
        display: block !important;
        visibility: visible !important;
      }
      
      /* Value text styling inside overlay */
      .live-price-value {
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        color: #fff !important;
        font-weight: bold !important;
        text-shadow: 0 0 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7) !important;
        font-family: var(--grist-font-family-data) !important;
        font-size: 12px !important;
        white-space: nowrap !important;
        z-index: 100 !important;
        transition: transform 0.2s ease-out !important;
        pointer-events: none !important;
        display: block !important;
        visibility: visible !important;
      }
      
      /* Custom midnight-blue background for position_size and trailing_stop_price */
      .live-price-overlay-container[data-field="position_size"],
      .live-price-overlay-container[data-field="trailing_stop_price"] {
        background: #191970 !important; /* midnight blue */
        box-shadow: none !important;
        border: 1px solid rgba(255,255,255,0.25) !important;
      }
      .live-price-overlay-container[data-field="position_size"] .live-price-value,
      .live-price-overlay-container[data-field="trailing_stop_price"] .live-price-value {
        color: #ffffff !important;
        text-shadow: none !important;
      }

      /* Latency display styling */
      .live-price-latency {
        position: absolute !important;
        top: 2px !important;
        right: 2px !important;
        color: rgba(255, 255, 255, 0.8) !important;
        font-size: 9px !important;
        font-weight: normal !important;
        text-shadow: 0 0 2px rgba(0,0,0,0.8) !important;
        font-family: var(--grist-font-family-data) !important;
        white-space: nowrap !important;
        z-index: 3 !important;
        pointer-events: none !important;
        opacity: 0.7 !important;
        transition: opacity 0.2s ease-out !important;
      }
      
      /* Hide latency display when no data */
      .live-price-latency:empty {
        display: none !important;
      }
      
      /* Positive price change - vibrant green */
      .live-price-overlay-container.price-up {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(0,255,0,0.9), rgba(0,200,0,0.8)) !important;
        box-shadow: 
          inset 0 0 25px rgba(0,255,0,0.7),
          0 0 12px rgba(0,255,0,0.5) !important;
        border: 1px solid rgba(0,255,0,0.4) !important;
      }
      
      /* Negative price change - vibrant red */
      .live-price-overlay-container.price-down {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(255,0,0,0.9), rgba(200,0,0,0.8)) !important;
        box-shadow: 
          inset 0 0 25px rgba(255,0,0,0.7),
          0 0 12px rgba(255,0,0,0.5) !important;
        border: 1px solid rgba(255,0,0,0.4) !important;
      }
      
      /* Neutral price - blue/gray */
      .live-price-overlay-container.price-neutral {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(120,120,160,0.8), rgba(100,100,140,0.7)) !important;
        box-shadow: 
          inset 0 0 20px rgba(150,150,200,0.5),
          0 0 10px rgba(120,120,160,0.4) !important;
        border: 1px solid rgba(150,150,200,0.3) !important;
      }
      
      /* Loading state - blue pulse */
      .live-price-overlay-container.loading {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(100,100,255,0.8), rgba(80,80,220,0.7)) !important;
        box-shadow: 
          inset 0 0 20px rgba(120,120,255,0.6),
          0 0 10px rgba(100,100,220,0.5) !important;
        border: 1px solid rgba(120,120,255,0.4) !important;
      }
      
      /* Volume overlays - orange theme */
      .live-price-overlay-container[data-field="v"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(255,165,0,0.8), rgba(255,140,0,0.7)) !important;
        box-shadow: 
          inset 0 0 20px rgba(255,165,0,0.6),
          0 0 10px rgba(255,165,0,0.4) !important;
        border: 1px solid rgba(255,165,0,0.4) !important;
      }
      
      /* Bid overlays - green theme */
      .live-price-overlay-container[data-field="bp1"], 
      .live-price-overlay-container[data-field="bq1"],
      .live-price-overlay-container[data-field="bp2"],
      .live-price-overlay-container[data-field="bq2"],
      .live-price-overlay-container[data-field="bp3"],
      .live-price-overlay-container[data-field="bq3"],
      .live-price-overlay-container[data-field="bp4"],
      .live-price-overlay-container[data-field="bq4"],
      .live-price-overlay-container[data-field="bp5"],
      .live-price-overlay-container[data-field="bq5"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(40,167,69,0.95), rgba(25,135,45,0.85)) !important;
        box-shadow: 
          inset 0 0 20px rgba(40,167,69,0.7),
          0 0 10px rgba(40,167,69,0.5) !important;
        border: 1px solid rgba(40,167,69,0.4) !important;
      }
      
      /* Ask overlays - red theme */
      .live-price-overlay-container[data-field="sp1"], 
      .live-price-overlay-container[data-field="sq1"],
      .live-price-overlay-container[data-field="sp2"],
      .live-price-overlay-container[data-field="sq2"],
      .live-price-overlay-container[data-field="sp3"],
      .live-price-overlay-container[data-field="sq3"],
      .live-price-overlay-container[data-field="sp4"],
      .live-price-overlay-container[data-field="sq4"],
      .live-price-overlay-container[data-field="sp5"],
      .live-price-overlay-container[data-field="sq5"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(220,53,69,0.95), rgba(185,35,50,0.85)) !important;
        box-shadow: 
          inset 0 0 20px rgba(220,53,69,0.7),
          0 0 10px rgba(220,53,69,0.5) !important;
        border: 1px solid rgba(220,53,69,0.4) !important;
      }
      
      /* OHLC overlays - purple theme */
      .live-price-overlay-container[data-field="h"], 
      .live-price-overlay-container[data-field="l"], 
      .live-price-overlay-container[data-field="o"], 
      .live-price-overlay-container[data-field="c"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(153,102,255,0.95), rgba(125,80,210,0.85)) !important;
        box-shadow: 
          inset 0 0 20px rgba(153,102,255,0.7),
          0 0 10px rgba(153,102,255,0.5) !important;
        border: 1px solid rgba(153,102,255,0.4) !important;
      }
      
      /* Circuit limits - yellow theme */
      .live-price-overlay-container[data-field="uc"],
      .live-price-overlay-container[data-field="lc"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(255,193,7,0.95), rgba(220,165,5,0.85)) !important;
        box-shadow: 
          inset 0 0 20px rgba(255,193,7,0.7),
          0 0 10px rgba(255,193,7,0.5) !important;
        border: 1px solid rgba(255,193,7,0.4) !important;
      }
      
      /* Average price - cyan theme */
      .live-price-overlay-container[data-field="ap"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(23,162,184,0.95), rgba(15,125,145,0.85)) !important;
        box-shadow: 
          inset 0 0 20px rgba(23,162,184,0.7),
          0 0 10px rgba(23,162,184,0.5) !important;
        border: 1px solid rgba(23,162,184,0.4) !important;
      }
      
      /* Open interest - magenta theme */
      .live-price-overlay-container[data-field="oi"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(255,0,255,0.95), rgba(200,0,200,0.85)) !important;
        box-shadow: 
          inset 0 0 20px rgba(255,0,255,0.7),
          0 0 10px rgba(255,0,255,0.5) !important;
        border: 1px solid rgba(255,0,255,0.4) !important;
      }
      
      /* Total buy/sell quantities - gradient themes */
      .live-price-overlay-container[data-field="tbq"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(40,167,69,0.85), rgba(25,135,45,0.75)) !important;
        box-shadow: 
          inset 0 0 20px rgba(40,167,69,0.6),
          0 0 10px rgba(40,167,69,0.4) !important;
        border: 1px solid rgba(40,167,69,0.4) !important;
      }
      
      .live-price-overlay-container[data-field="tsq"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(220,53,69,0.85), rgba(185,35,50,0.75)) !important;
        box-shadow: 
          inset 0 0 20px rgba(220,53,69,0.6),
          0 0 10px rgba(220,53,69,0.4) !important;
        border: 1px solid rgba(220,53,69,0.4) !important;
      }
      
      /* Level badges for depth overlays */
      .live-price-overlay-container[data-field*="2"]:before,
      .live-price-overlay-container[data-field*="3"]:before,
      .live-price-overlay-container[data-field*="4"]:before,
      .live-price-overlay-container[data-field*="5"]:before {
        content: attr(data-level) !important;
        position: absolute !important;
        top: 2px !important;
        left: 2px !important;
        background: rgba(255,255,255,0.2) !important;
        color: #fff !important;
        font-size: 8px !important;
        font-weight: bold !important;
        padding: 1px 3px !important;
        border-radius: 2px !important;
        z-index: 4 !important;
        pointer-events: none !important;
      }
      
      /* Add level badges content — Level 2-5 order-book depth ONLY.
         Use $= (ends with) so indicator_ema20 / indicator_rsi14 / indicator_atr14
         don't wrongly pick up "L2" / "L4" badges because their names contain a 2 or 4. */
      .live-price-overlay-container[data-field$="_l2"]:before { content: "L2" !important; }
      .live-price-overlay-container[data-field$="_l3"]:before { content: "L3" !important; }
      .live-price-overlay-container[data-field$="_l4"]:before { content: "L4" !important; }
      .live-price-overlay-container[data-field$="_l5"]:before { content: "L5" !important; }
      
      /* Price change overlays - special color based on positive/negative */
      .live-price-overlay-container[data-field="pc"],
      .live-price-overlay-container[data-field="pcp"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(138,43,226,0.85), rgba(106,90,205,0.75)) !important;
        box-shadow: 
          inset 0 0 20px rgba(138,43,226,0.6),
          0 0 10px rgba(138,43,226,0.4) !important;
        border: 1px solid rgba(138,43,226,0.4) !important;
      }
      
      /* Additional trade data overlays */
      .live-price-overlay-container[data-field="ltt"],
      .live-price-overlay-container[data-field="ltq"],
      .live-price-overlay-container[data-field="ttv"] {
        background: 
          linear-gradient(135deg, rgba(0,0,0,0.92), rgba(0,0,0,0.85)),
          linear-gradient(135deg, rgba(108,117,125,0.85), rgba(80,90,100,0.75)) !important;
        box-shadow: 
          inset 0 0 20px rgba(108,117,125,0.6),
          0 0 10px rgba(108,117,125,0.4) !important;
        border: 1px solid rgba(108,117,125,0.4) !important;
      }

      /* Order overlays - SUPER BRIGHT themes for trading data */
      .live-price-overlay-container[data-field="order_status"] {
        background: 
          linear-gradient(135deg, rgba(13,110,253,0.95), rgba(0,90,255,0.9)) !important;
        box-shadow: 
          inset 0 0 30px rgba(13,110,253,0.9),
          0 0 20px rgba(13,110,253,0.8),
          0 0 30px rgba(0,90,255,0.6) !important;
        border: 3px solid rgba(13,110,253,0.9) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-blue 2s ease-in-out infinite alternate !important;
      }

      .live-price-overlay-container[data-field="order_trantype"] {
        background: 
          linear-gradient(135deg, rgba(255,193,7,0.95), rgba(255,140,0,0.9)) !important;
        box-shadow: 
          inset 0 0 30px rgba(255,193,7,0.9),
          0 0 20px rgba(255,193,7,0.8),
          0 0 30px rgba(255,140,0,0.6) !important;
        border: 3px solid rgba(255,193,7,0.9) !important;
        color: black !important;
        font-weight: bold !important;
        animation: glow-gold 2s ease-in-out infinite alternate !important;
      }

      .live-price-overlay-container[data-field="order_qty"],
      .live-price-overlay-container[data-field="order_fillshares"] {
        background: 
          linear-gradient(135deg, rgba(108,117,125,0.95), rgba(85,95,105,0.9)) !important;
        box-shadow: 
          inset 0 0 30px rgba(108,117,125,0.9),
          0 0 20px rgba(108,117,125,0.8),
          0 0 30px rgba(85,95,105,0.6) !important;
        border: 3px solid rgba(108,117,125,0.9) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-gray 2s ease-in-out infinite alternate !important;
      }

      .live-price-overlay-container[data-field="order_prc"],
      .live-price-overlay-container[data-field="order_avgprc"],
      .live-price-overlay-container[data-field="order_trgprc"] {
        background: 
          linear-gradient(135deg, rgba(32,201,151,0.95), rgba(25,160,120,0.9)) !important;
        box-shadow: 
          inset 0 0 30px rgba(32,201,151,0.9),
          0 0 20px rgba(32,201,151,0.8),
          0 0 30px rgba(25,160,120,0.6) !important;
        border: 3px solid rgba(32,201,151,0.9) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-green 2s ease-in-out infinite alternate !important;
      }

      .live-price-overlay-container[data-field="order_pcode"],
      .live-price-overlay-container[data-field="order_ret"],
      .live-price-overlay-container[data-field="order_fltm"] {
        background: 
          linear-gradient(135deg, rgba(111,66,193,0.95), rgba(90,50,160,0.9)) !important;
        box-shadow: 
          inset 0 0 30px rgba(111,66,193,0.9),
          0 0 20px rgba(111,66,193,0.8),
          0 0 30px rgba(90,50,160,0.6) !important;
        border: 3px solid rgba(111,66,193,0.9) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-purple 2s ease-in-out infinite alternate !important;
      }
      
      /* Trading action status overlays - Ultra bright for instant feedback */
      .live-price-overlay-container[data-field="place_order_status"] {
        background: 
          linear-gradient(135deg, rgba(40,167,69,0.98), rgba(25,135,45,0.95)) !important;
        box-shadow: 
          inset 0 0 40px rgba(40,167,69,1),
          0 0 30px rgba(40,167,69,0.9),
          0 0 50px rgba(25,135,45,0.8) !important;
        border: 4px solid rgba(40,167,69,1) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-green-intense 1.5s ease-in-out infinite alternate !important;
      }
      
      .live-price-overlay-container[data-field="cancel_order_status"] {
        background: 
          linear-gradient(135deg, rgba(220,53,69,0.98), rgba(185,35,50,0.95)) !important;
        box-shadow: 
          inset 0 0 40px rgba(220,53,69,1),
          0 0 30px rgba(220,53,69,0.9),
          0 0 50px rgba(185,35,50,0.8) !important;
        border: 4px solid rgba(220,53,69,1) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-red-intense 1.5s ease-in-out infinite alternate !important;
      }
      
      .live-price-overlay-container[data-field="trail_status"] {
        background: 
          linear-gradient(135deg, rgba(111,66,193,0.98), rgba(90,50,160,0.95)) !important;
        box-shadow: 
          inset 0 0 40px rgba(111,66,193,1),
          0 0 30px rgba(111,66,193,0.9),
          0 0 50px rgba(90,50,160,0.8) !important;
        border: 4px solid rgba(111,66,193,1) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-purple-intense 1.5s ease-in-out infinite alternate !important;
      }
      
      /* Trailing stop price and percentage overlays */
      .live-price-overlay-container[data-field="trailing_price"] {
        background: 
          linear-gradient(135deg, rgba(255,193,7,0.98), rgba(255,140,0,0.95)) !important;
        box-shadow: 
          inset 0 0 40px rgba(255,193,7,1),
          0 0 30px rgba(255,193,7,0.9),
          0 0 50px rgba(255,140,0,0.8) !important;
        border: 4px solid rgba(255,193,7,1) !important;
        color: black !important;
        font-weight: bold !important;
        animation: glow-gold-intense 2s ease-in-out infinite alternate !important;
      }
      
      .live-price-overlay-container[data-field="trailing_pct"] {
        background: 
          linear-gradient(135deg, rgba(23,162,184,0.98), rgba(15,125,145,0.95)) !important;
        box-shadow: 
          inset 0 0 40px rgba(23,162,184,1),
          0 0 30px rgba(23,162,184,0.9),
          0 0 50px rgba(15,125,145,0.8) !important;
        border: 4px solid rgba(23,162,184,1) !important;
        color: white !important;
        font-weight: bold !important;
        animation: glow-cyan-intense 2s ease-in-out infinite alternate !important;
      }
      
      /* Hover effects for all overlays */
      .live-price-overlay-container:hover {
        transform: scale(1.02) !important;
        box-shadow: 
          0 0 20px rgba(255,255,255,0.3) !important;
        z-index: 15 !important;
      }
      
      /* Updating animation */
      .live-price-overlay-container.updating {
        animation: pulse-update 0.3s ease-in-out !important;
      }
      
      @keyframes pulse-update {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      /* Order overlay glow animations */
      @keyframes glow-blue {
        from { box-shadow: inset 0 0 30px rgba(13,110,253,0.9), 0 0 20px rgba(13,110,253,0.8), 0 0 30px rgba(0,90,255,0.6); }
        to { box-shadow: inset 0 0 40px rgba(13,110,253,1), 0 0 30px rgba(13,110,253,0.9), 0 0 50px rgba(0,90,255,0.8); }
      }

      @keyframes glow-gold {
        from { box-shadow: inset 0 0 30px rgba(255,193,7,0.9), 0 0 20px rgba(255,193,7,0.8), 0 0 30px rgba(255,140,0,0.6); }
        to { box-shadow: inset 0 0 40px rgba(255,193,7,1), 0 0 30px rgba(255,193,7,0.9), 0 0 50px rgba(255,140,0,0.8); }
      }

      @keyframes glow-gray {
        from { box-shadow: inset 0 0 30px rgba(108,117,125,0.9), 0 0 20px rgba(108,117,125,0.8), 0 0 30px rgba(85,95,105,0.6); }
        to { box-shadow: inset 0 0 40px rgba(108,117,125,1), 0 0 30px rgba(108,117,125,0.9), 0 0 50px rgba(85,95,105,0.8); }
      }

      @keyframes glow-green {
        from { box-shadow: inset 0 0 30px rgba(32,201,151,0.9), 0 0 20px rgba(32,201,151,0.8), 0 0 30px rgba(25,160,120,0.6); }
        to { box-shadow: inset 0 0 40px rgba(32,201,151,1), 0 0 30px rgba(32,201,151,0.9), 0 0 50px rgba(25,160,120,0.8); }
      }

      @keyframes glow-purple {
        from { box-shadow: inset 0 0 30px rgba(111,66,193,0.9), 0 0 20px rgba(111,66,193,0.8), 0 0 30px rgba(90,50,160,0.6); }
        to { box-shadow: inset 0 0 40px rgba(111,66,193,1), 0 0 30px rgba(111,66,193,0.9), 0 0 50px rgba(90,50,160,0.8); }
      }
      
      /* Intense glow animations for trading actions */
      @keyframes glow-green-intense {
        from { box-shadow: inset 0 0 40px rgba(40,167,69,1), 0 0 30px rgba(40,167,69,0.9), 0 0 50px rgba(25,135,45,0.8); }
        to { box-shadow: inset 0 0 60px rgba(40,167,69,1), 0 0 50px rgba(40,167,69,1), 0 0 80px rgba(25,135,45,1); }
      }
      
      @keyframes glow-red-intense {
        from { box-shadow: inset 0 0 40px rgba(220,53,69,1), 0 0 30px rgba(220,53,69,0.9), 0 0 50px rgba(185,35,50,0.8); }
        to { box-shadow: inset 0 0 60px rgba(220,53,69,1), 0 0 50px rgba(220,53,69,1), 0 0 80px rgba(185,35,50,1); }
      }
      
      @keyframes glow-purple-intense {
        from { box-shadow: inset 0 0 40px rgba(111,66,193,1), 0 0 30px rgba(111,66,193,0.9), 0 0 50px rgba(90,50,160,0.8); }
        to { box-shadow: inset 0 0 60px rgba(111,66,193,1), 0 0 50px rgba(111,66,193,1), 0 0 80px rgba(90,50,160,1); }
      }
      
      @keyframes glow-gold-intense {
        from { box-shadow: inset 0 0 40px rgba(255,193,7,1), 0 0 30px rgba(255,193,7,0.9), 0 0 50px rgba(255,140,0,0.8); }
        to { box-shadow: inset 0 0 60px rgba(255,193,7,1), 0 0 50px rgba(255,193,7,1), 0 0 80px rgba(255,140,0,1); }
      }
      
      @keyframes glow-cyan-intense {
        from { box-shadow: inset 0 0 40px rgba(23,162,184,1), 0 0 30px rgba(23,162,184,0.9), 0 0 50px rgba(15,125,145,0.8); }
        to { box-shadow: inset 0 0 60px rgba(23,162,184,1), 0 0 50px rgba(23,162,184,1), 0 0 80px rgba(15,125,145,1); }
      }
      
      /* Neutralize default red formula text colour for hot columns */
      .field_clip .formula_text,
      .field_clip .formula_field {
        color: inherit !important;
      }
      
      /* Hide original cell text when overlay is present - overlay shows the data */
      .field_clip[data-has-overlay="true"] .cell_value,
      .field_clip[data-has-overlay="true"] .formula_text,
      .field_clip[data-has-overlay="true"] .formula_field,
      .field_clip[data-has-overlay="true"] .formula_error,
      .field_clip[data-has-overlay="true"] .error_text,
      .field_clip[data-has-overlay="true"] .cellerror {
        opacity: 0 !important;
        visibility: hidden !important;
      }
      
      /* Ensure overlay text is clearly visible */
      .live-price-value {
        color: #fff !important;
        font-weight: bold !important;
        text-shadow: 0 0 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7) !important;
        z-index: 10 !important;
        pointer-events: none !important;
      }
      
      /* HTML: <div class="loader"></div> */
      .loader {
        height: 3px;
        width: 75px;
        --c:no-repeat linear-gradient(#000 0 0);
        background: var(--c), var(--c), var(--c), var(--c);
        background-size: 21% 100%;
        animation: l7 1s infinite alternate;
      }
      @keyframes l7 {
        0%,20%  {background-position: calc(0*100%/4) 0,calc(1*100%/4) 0,calc(2*100%/4) 0,calc(3*100%/4) 0}
        35%     {background-position: calc(0*100%/4) 0,calc(1*100%/4) 0,calc(2*100%/4) 0,calc(4*100%/4) 0}
        50%     {background-position: calc(0*100%/4) 0,calc(1*100%/4) 0,calc(3*100%/4) 0,calc(4*100%/4) 0}
        65%     {background-position: calc(0*100%/4) 0,calc(2*100%/4) 0,calc(3*100%/4) 0,calc(4*100%/4) 0}
        80%,100%{background-position: calc(1*100%/4) 0,calc(2*100%/4) 0,calc(3*100%/4) 0,calc(4*100%/4) 0}
      }
      
      /* Add rule for transparent overlay */
      .field_clip[data-has-overlay="true"] {
        color: transparent !important;
      }
    `;
    
    // Remove existing style if present
    const existingStyle = document.getElementById('simple-live-overlay-styles');
    if (existingStyle) {
      existingStyle.remove();
      }
    
    document.head.appendChild(style);
  }

  private async _fetchInitialPrices(): Promise<void> {
    const symbols = Array.from(this._subscribedSymbols);
    if (symbols.length === 0) return;

    // Expand symbols to include -EQ variants for better matching
    const expandedSymbols = new Set<string>();
    symbols.forEach(symbol => {
      expandedSymbols.add(symbol);
      expandedSymbols.add(`${symbol}-EQ`);
      if (symbol.includes('-')) {
        expandedSymbols.add(symbol.split('-')[0]);
      }
    });

    try {
      console.log(`🚀 Fetching initial data for ${expandedSymbols.size} symbols (expanded from ${symbols.length})`);
      
      // Fetch live market data only; order fields arrive via WebSocket
      await this._fetchMarketData(Array.from(expandedSymbols));
    } catch (error) {
      console.error('❌ Error fetching initial data:', error);
    }
  }

  private async _fetchMarketData(symbols: string[]): Promise<void> {
    try {
      // Use the new fields API endpoint that fetches all field data
      const response = await fetch('/api/live-fields/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols })
      });

      if (response.ok) {
        const fieldData = await response.json();
        
        console.log(`📊 Received data for ${Object.keys(fieldData).length} symbols`);
        
                 // Create mapping from original symbols to response data
         const symbolMapping = new Map<string, any>();
         symbols.forEach(originalSymbol => {
           // Find the best matching response
           for (const [responseKey, responseData] of Object.entries(fieldData)) {
             if (responseData && (responseData as any).ltp !== undefined) {
               const actualSymbol = (responseData as any).symbol;
               
               // Check multiple symbol variations for matching
               const cleanOriginal = originalSymbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
               const cleanActual = actualSymbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
               
               if (cleanOriginal === cleanActual ||
                   actualSymbol === originalSymbol || 
                   actualSymbol === `${originalSymbol}-EQ` ||
                   actualSymbol.replace('-EQ', '') === originalSymbol ||
                   originalSymbol.replace('-EQ', '') === actualSymbol.replace('-EQ', '') ||
                   responseKey.includes(cleanOriginal) ||
                   responseKey.includes(originalSymbol)) {
                 symbolMapping.set(originalSymbol, responseData);
                 console.log(`✅ Mapped ${originalSymbol} to data from ${actualSymbol} (key: ${responseKey})`);
                 break;
               }
             }
           }
         });
        
        // Process mapped data
        symbolMapping.forEach((data, originalSymbol) => {
          // Process LTP data for existing price overlay system
          if (data.ltp !== undefined) {
            const priceUpdate = {
              symbol: originalSymbol, // Use original symbol for consistency
              exchange: data.exchange,
              price: data.ltp,
              change: data.pc || 0,
              changePercent: data.pcp || 0,
              timestamp: data.timestamp || Date.now()
            };
            const cacheKey = `${originalSymbol}-${data.exchange}`;
            this._priceCache.set(cacheKey, priceUpdate);
            this._updateCellsForSymbol(originalSymbol, data.exchange, priceUpdate);
          }

          // Process other field data for overlays
          Object.entries(data).forEach(([field, value]: [string, any]) => {
            if (field !== 'symbol' && field !== 'exchange' && field !== 'timestamp' && value !== undefined) {
              // Update overlays for this specific field using original symbol
              this._updateCellsForField(originalSymbol, data.exchange, field, value);
            }
          });
        });
        
        console.log(`🎯 Processed data for ${symbolMapping.size} symbols`);
      } else {
        console.error('Failed to fetch initial field data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching initial field data:', error);
    }
  }

  // @ts-ignore: suppress unused method warning
  private async _fetchOrderData(symbols: string[]): Promise<void> {
    try {
      console.log(`🔍 ORDER DEBUG: Fetching order data for symbols:`, symbols);
      
      // Fetch order data from the order data API
      const response = await fetch('/api/docs/current/order-data', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const orderData = await response.json();
        console.log(`🔍 ORDER DEBUG: Received ${orderData.length} orders from API`);
        
        // Log available symbols for debugging
        const availableSymbols = orderData.map((order: any) => order.symbol).filter(Boolean);
        console.log(`🔍 ORDER DEBUG: Available symbols:`, availableSymbols.slice(0,10));
        
        // Enhanced symbol matching - handle -EQ suffix and other variations
        for (const requestedSymbol of symbols) {
          console.log(`🔍 ORDER DEBUG: Processing symbol: ${requestedSymbol}`);
          let matchingOrders = [];
          
          // Try exact match first
          matchingOrders = orderData.filter((order: any) => 
            order.symbol && order.symbol.toUpperCase() === requestedSymbol.toUpperCase()
          );
          
          if (matchingOrders.length > 0) {
            console.log(`🔍 ORDER DEBUG: Exact match for ${requestedSymbol}: ${matchingOrders.length} orders`);
          }
          
          // If no exact match, try adding -EQ suffix
          if (matchingOrders.length === 0) {
            const symbolWithEQ = requestedSymbol.toUpperCase() + '-EQ';
            matchingOrders = orderData.filter((order: any) => 
              order.symbol && order.symbol.toUpperCase() === symbolWithEQ
            );
            if (matchingOrders.length > 0) {
              console.log(`🔍 ORDER DEBUG: -EQ match: ${requestedSymbol} -> ${symbolWithEQ} (${matchingOrders.length} orders)`);
            }
          }
          
          // If still no match, try removing -EQ suffix if present
          if (matchingOrders.length === 0 && requestedSymbol.toUpperCase().endsWith('-EQ')) {
            const symbolWithoutEQ = requestedSymbol.slice(0, -3);
            matchingOrders = orderData.filter((order: any) => 
              order.symbol && order.symbol.toUpperCase() === symbolWithoutEQ.toUpperCase()
            );
            if (matchingOrders.length > 0) {
              console.log(`🔍 ORDER DEBUG: Remove -EQ match: ${requestedSymbol} -> ${symbolWithoutEQ} (${matchingOrders.length} orders)`);
            }
          }
          
          // If still no match, try partial matching
          if (matchingOrders.length === 0) {
            matchingOrders = orderData.filter((order: any) => {
              if (!order.symbol) return false;
              const orderSymbol = order.symbol.toUpperCase();
              const searchSymbol = requestedSymbol.toUpperCase();
              return orderSymbol.includes(searchSymbol) || searchSymbol.includes(orderSymbol);
            });
            if (matchingOrders.length > 0) {
              console.log(`🔍 ORDER DEBUG: Partial match for ${requestedSymbol}: ${matchingOrders.length} orders`);
            }
          }
          
          // Update overlays for all matching orders
          if (matchingOrders.length > 0) {
            console.log(`🔍 ORDER DEBUG: Updating overlays for ${requestedSymbol} with ${matchingOrders.length} orders`);
            for (const order of matchingOrders) {
              this._updateOrderFields(requestedSymbol, order.exchange || 'NSE', order);
            }
          } else {
            console.log(`🔍 ORDER DEBUG: No order data found for symbol: ${requestedSymbol}`);
          }
        }
      } else {
        console.error('🔍 ORDER DEBUG: Failed to fetch order data:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('🔍 ORDER DEBUG: Error fetching order data:', error);
    }
  }

  private _updateOrderFields(symbol: string, exchange: string, orderData: any): void {
    console.log(`📋 _updateOrderFields called: symbol="${symbol}", exchange="${exchange}"`);
    console.log(`📋 Order data:`, orderData);
    
    // Update each order field with the appropriate value
    const orderFields = {
      'order_status': orderData.status,
      'order_trantype': orderData.trantype,
      'order_qty': orderData.qty,
      'order_fillshares': orderData.fillshares,
      'order_prc': orderData.prc,
      'order_avgprc': orderData.avgprc,
      'order_trgprc': orderData.trgprc,
      'order_pcode': orderData.pcode,
      'order_ret': orderData.ret,
      'order_fltm': orderData.fltm
    };
    
    console.log(`📋 Processing order fields for ${symbol}:`, orderFields);
    
    Object.entries(orderFields).forEach(([field, value]) => {
      if (value !== undefined && value !== null) {
        console.log(`📋 Updating field ${field} with value:`, value);
        this._updateCellsForField(symbol, exchange, field, value);
      }
    });
  }

  private _updateCellsForSymbol(symbol: string, exchange: string, update: PriceUpdate): void {
    // This method should ONLY update overlays with field='ltp' (price overlays)
    // Other fields should be handled by _updateCellsForField
    const symbolUpper = symbol.toUpperCase();
    const overlaySet = this._symbolToOverlays.get(symbolUpper);
    let foundCells = 0;
    
    console.log(`🎯 Updating PRICE cells for ${symbolUpper}-${exchange}: overlaySet exists = ${!!overlaySet}, size = ${overlaySet?.size || 0}`);
    
    if (overlaySet) {
      overlaySet.forEach(overlay => {
        const overlayField = overlay.getAttribute('data-field');
        const overlayExchange = (overlay.getAttribute('data-exchange') || 'ANY').toUpperCase();
        if (overlayExchange !== 'ANY' && overlayExchange !== exchange.toUpperCase()) {
          return;
        }

        if (overlayField === 'ltp') {
          const cell = overlay.parentElement as HTMLElement;
          if (cell) {
            console.log(`📍 Found PRICE overlay for ${symbolUpper}, updating cell:`, cell);
            this._updateCellOverlayDirect(cell, update);
            foundCells++;
          }
        } else if (overlayField === 'pnl_realtime') {
          // LTP-tick path for pnl_realtime is DISABLED — server `_directPnLUpdate`
          // pushes authoritative values on pnl_realtime field via the overlay
          // channel. That handler uses live Redis qty/avg, not the potentially
          // stale `data-pnl-avg/qty` cell attributes that get baked at formula
          // eval time. Two paths racing caused flicker.
          // (No-op here — formatter in _updateCellsForField does all pnl_realtime rendering.)
        }
      });
    }
    
    console.log(`🔍 Updated ${foundCells} PRICE cells for ${symbolUpper} (field=ltp only)`);
    
    if (foundCells === 0) {
      console.warn(`⚠️ No ltp overlays found for ${symbolUpper}`);
      console.log(`📊 Current symbol map:`, Array.from(this._symbolToOverlays.keys()).sort());
    }
  }

  private _updateCellOverlayDirect(cell: HTMLElement, update: PriceUpdate): void {
    // Find the overlay in this cell
    const overlay = cell.querySelector('.live-price-overlay-container') as HTMLElement;
    if (!overlay) {
      // Check if this cell actually should have an overlay by looking for formula patterns
      const cellText = cell.textContent || '';
      const hasFormula = cellText.includes('LIVE_') || cellText.includes('GET_POSITION_SIZE') || cellText.includes('TRAILING_STOP_PRICE') || cellText.includes('DIST_FROM_STOP') || cellText.includes('LIVE_PNL') || cellText.includes('ATR_');
      
      if (hasFormula) {
        console.warn('⚠️ Overlay missing for formula cell:', update.symbol, 'Text:', cellText.substring(0, 50));
        // Try to create overlay if formula is detected
        this._scanForNewFormulas();
      } else {
        // Only log error if this seems like it should have an overlay
        console.debug('ℹ️ No overlay found for', update.symbol, '(cell may not contain relevant formula)');
      }
      return;
    }

    // Ensure exchange matches this overlay
    const overlayExchange = (overlay.getAttribute('data-exchange') || 'ANY').toUpperCase();
    if (overlayExchange !== 'ANY' && overlayExchange !== (update.exchange || '').toUpperCase()) {
      return;
    }
    
    const priceDisplay = overlay.querySelector('.live-price-value') as HTMLElement;
    if (!priceDisplay) {
      console.error('❌ Price display not found in overlay for', update.symbol);
      return;
    }
    
    const latencyDisplay = overlay.querySelector('.live-price-latency') as HTMLElement;
    if (!latencyDisplay) {
      console.error('❌ Latency display not found in overlay for', update.symbol);
      return;
    }
    
    // Update price text
    const newPrice = (update.price !== undefined && update.price !== null) ? update.price.toFixed(2) : 'N/A';
    const oldPrice = priceDisplay.textContent;
    
    // Fix change calculation if server sent change=0 but price moved
    let actualChange = update.change;
    if (actualChange === 0 && oldPrice && oldPrice !== 'Loading...' && update.price !== undefined && update.price !== null) {
      const oldPriceNum = parseFloat(oldPrice);
      if (!isNaN(oldPriceNum)) {
        actualChange = update.price - oldPriceNum;
      }
    }
    
    priceDisplay.textContent = newPrice;
    
    // Update latency display
    if (update.latency && update.latency.endToEndLatency !== undefined) {
      const latencyMs = update.latency.endToEndLatency;
      if (latencyMs < 1000) {
        latencyDisplay.textContent = `${latencyMs}ms`;
        latencyDisplay.style.color = latencyMs < 100 ? 'rgba(0,255,0,0.8)' : 
                                    latencyMs < 500 ? 'rgba(255,255,0,0.8)' : 
                                    'rgba(255,128,0,0.8)';
      } else {
        latencyDisplay.textContent = `${(latencyMs / 1000).toFixed(1)}s`;
        latencyDisplay.style.color = 'rgba(255,0,0,0.8)';
      }
    } else {
      latencyDisplay.textContent = '';
    }
    
    // Reveal overlay if it was hidden awaiting price
    overlay.style.visibility = 'visible';
    const key = `${update.symbol}-${update.exchange}`;
    this._pendingFetch.delete(key);
    
    // Determine new price class
    let newPriceClass = 'price-neutral';
    if (actualChange > 0) {
      newPriceClass = 'price-up';
    } else if (actualChange < 0) {
      newPriceClass = 'price-down';
    }
    
    // Only update classes if they changed
    const currentClasses = overlay.className;
    const hasCorrectPriceClass = currentClasses.includes(newPriceClass);
    
    if (!hasCorrectPriceClass || currentClasses.includes('loading')) {
      overlay.className = `live-price-overlay-container ${newPriceClass}`;
      
      overlay.classList.add('updating');
      requestAnimationFrame(() => {
          overlay.classList.remove('updating');
      });
    }
  }

  public dispose(): void {
    console.log('SimpleLiveOverlay: Disposing...');

    // Disconnect observers
    if (this._observer) {
      this._observer.disconnect();
    }

    // Unsubscribe from overlay comm
    if (this._overlayComm) {
      this._overlayComm.unsubscribe(Array.from(this._subscribedSymbols));
    }

    // Remove style element
    const styleElement = document.getElementById('simple-live-overlay-styles');
    if (styleElement) {
      styleElement.remove();
    }

    super.dispose();
  }

  private _handlePriceUpdate(update: PriceUpdate): void {
    const clientReceivedTime = Date.now();
    
    // Calculate and enhance latency metrics
    if (update.latency) {
      const latencyData = update.latency;
      const clientLatency = latencyData.clientSendTime ? clientReceivedTime - latencyData.clientSendTime : undefined;
      const endToEndLatency = latencyData.wsReceivedTime ? clientReceivedTime - latencyData.wsReceivedTime : undefined;
      
      const enhancedUpdate = {
        ...update,
        latency: {
          ...latencyData,
          clientReceivedTime,
          clientLatency,
          endToEndLatency,
          totalLatency: endToEndLatency || latencyData.totalLatency
        }
      };
      
    const key = `${update.symbol}-${update.exchange}`;
      this._priceCache.set(key, enhancedUpdate);
      this._updateCellsForSymbol(update.symbol, update.exchange, enhancedUpdate);
    } else {
      const key = `${update.symbol}-${update.exchange}`;
      this._priceCache.set(key, update);
    this._updateCellsForSymbol(update.symbol, update.exchange, update);
    }
  }

  private _handleGenericFieldUpdate(update: GenericFieldUpdate): void {
    const key = `${update.symbol}-${update.exchange}`;
    const cached = this._priceCache.get(key) || { symbol: update.symbol, exchange: update.exchange } as any;
    cached[update.field] = update.value;
    this._priceCache.set(key, cached as any);
    
    // Debug order field updates
    if (update.field.startsWith('order_')) {
      console.log(`🔥 Received order field update: ${update.symbol} ${update.field}=${update.value}`);
      console.log(`📊 Cached data for ${key}:`, cached);
    }
    
    this._updateCellsForField(update.symbol, update.exchange, update.field, update.value);
  }

  private _updateCellsForField(symbol: string, exchange: string, field: string, value: number | string) {
    const symbolUpper = symbol.toUpperCase();
    const overlaySet = this._symbolToOverlays.get(symbolUpper);
    if (!overlaySet) {
      if (field.startsWith('order_')) {
        console.log(`⚠️ No overlay set found for ORDER field: ${symbolUpper} ${field}`);
        console.log(`📋 Available overlay symbols:`, Array.from(this._symbolToOverlays.keys()));
      }
      return;
    }
    
    console.log(`🎯 Updating ${overlaySet.size} overlays for ${symbolUpper}:${field}=${value}`);
    
    let updatedCount = 0;
    
    overlaySet.forEach(overlay => {
      const overlayField = overlay.getAttribute('data-field');
      // Filter by exchange for non-price fields as well
      const overlayExchange = (overlay.getAttribute('data-exchange') || 'ANY').toUpperCase();
      if (overlayExchange !== 'ANY' && overlayExchange !== exchange.toUpperCase()) {
        return;
      }
      
      if (overlayField === field) {
        const valueDisplay = overlay.querySelector('.live-price-value') as HTMLElement;
        if (valueDisplay) {
          let formattedValue = '';
          
          // Handle order fields first
          if (field.startsWith('order_')) {
            const strValue = String(value);
            if (field === 'order_status') {
              formattedValue = strValue; // Status is a string (OPEN, COMPLETE, etc.)
            } else if (field === 'order_trantype') {
              formattedValue = strValue === 'B' ? 'BUY' : strValue === 'S' ? 'SELL' : strValue;
            } else if (field === 'order_qty' || field === 'order_fillshares') {
              // Quantity fields as integers
              const numValue = Number(value);
              formattedValue = numValue === 0 ? '-' : Math.round(numValue).toLocaleString();
            } else if (field === 'order_prc' || field === 'order_avgprc' || field === 'order_trgprc') {
              // Price fields with 2 decimal places
              const numValue = Number(value);
              formattedValue = (numValue === 0 || isNaN(numValue)) ? '-' : numValue.toFixed(2);
            } else if (field === 'order_pcode') {
              // Product code (C, M, I, etc.)
              formattedValue = strValue;
            } else if (field === 'order_ret') {
              // Retention type (DAY, IOC, etc.)
              formattedValue = strValue;
            } else if (field === 'order_fltm') {
              // Fill time (timestamp or time string)
              formattedValue = strValue;
            } else {
              formattedValue = strValue;
            }
          } else if (field === 'tsl_order_status') {
            // Handle TSL order status field (string values like "pending", "completed", "rejected")
            const strValue = String(value);
            if (strValue === '' || strValue === 'null' || strValue === 'undefined') {
              formattedValue = '-'; // No order yet
            } else {
              // Show status with appropriate styling
              formattedValue = strValue;
              // Add CSS class for status styling
              if (valueDisplay.parentElement) {
                valueDisplay.parentElement.setAttribute('data-status', strValue);
              }
            }
          } else if (field === 'trailing_stop_price') {
            // Handle trailing stop price field - only show when TSL is active
            if (value === '' || value === null || value === undefined || value === 0) {
              formattedValue = '-'; // TSL not active or no price yet
            } else {
              const numValue = Number(value);
              formattedValue = numValue.toFixed(2);
            }
          } else if (field === 'pnl_realtime') {
            // Server-pushed {pnl, pct, avg, qty} — bypasses stale Grist placeholder
            // that bakes avg/qty at formula-eval time. Works even if the cell was
            // created before user opened the position for that symbol.
            //
            // Also: Grist flags the string placeholder as a type error in Numeric
            // columns and paints a red/error BG on the cell. We force the overlay
            // to a solid price-up/down/neutral class so the Grist BG never shows
            // through — fixes the "red BG on positive P&L" bug.
            let pnl = NaN, pct = NaN;
            if (value && typeof value === 'object') {
              pnl = Number((value as any).pnl);
              pct = Number((value as any).pct);
            }
            if (isNaN(pnl) || isNaN(pct)) {
              formattedValue = '-';
              // Neutral container so Grist error BG doesn't leak through
              const overlay = valueDisplay?.parentElement as HTMLElement | null;
              if (overlay) overlay.className = 'live-price-overlay-container price-neutral';
            } else {
              const sign = pnl >= 0 ? '+' : '';
              formattedValue = `${sign}₹${Math.round(pnl).toLocaleString('en-IN')} (${sign}${pct.toFixed(2)}%)`;
              if (valueDisplay) {
                // Override loading/price-down inherited class with correct sign-based class
                const overlay = valueDisplay.parentElement as HTMLElement | null;
                if (overlay) {
                  const sideClass = pnl > 0 ? 'price-up' : (pnl < 0 ? 'price-down' : 'price-neutral');
                  overlay.className = `live-price-overlay-container ${sideClass}`;
                  overlay.setAttribute('data-field', 'pnl_realtime'); // preserve field marker
                }
                valueDisplay.style.fontWeight = '700';
                valueDisplay.style.color = '#ffffff'; // white on colored BG (readable on both up/down)
                valueDisplay.style.fontVariantNumeric = 'tabular-nums';
                valueDisplay.style.textShadow = '0 1px 2px rgba(0,0,0,0.5)';
              }
            }
          } else if (field === 'atr_dist_pct') {
            // ATR distance from stop — same 5-tier pill as dist_from_stop_pct
            if (value === '' || value === null || value === undefined) {
              formattedValue = '-';
            } else {
              const n = Number(value);
              if (isNaN(n)) {
                formattedValue = '-';
              } else {
                formattedValue = (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
                if (valueDisplay) {
                  let fg: string, bg: string;
                  if (n >= 1.0) { fg = '#ffffff'; bg = '#15803d'; }
                  else if (n >= 0.5) { fg = '#ffffff'; bg = '#16a34a'; }
                  else if (n >= 0.2) { fg = '#422006'; bg = '#fbbf24'; }
                  else if (n >= 0) { fg = '#ffffff'; bg = '#ea580c'; }
                  else { fg = '#ffffff'; bg = '#dc2626'; }
                  valueDisplay.style.color = fg;
                  valueDisplay.style.background = bg;
                  valueDisplay.style.padding = '1px 6px';
                  valueDisplay.style.borderRadius = '3px';
                  valueDisplay.style.fontWeight = '700';
                  valueDisplay.style.fontVariantNumeric = 'tabular-nums';
                }
              }
            }
          } else if (field === 'atr_stop_price' || field === 'atr_hwm' ||
                     field === 'indicator_ema20' || field === 'indicator_atr14') {
            if (value === '' || value === null || value === undefined) {
              formattedValue = '-';
            } else {
              const n = Number(value);
              formattedValue = (isNaN(n) || n === 0) ? '-' : n.toFixed(2);
              if (valueDisplay) valueDisplay.style.fontVariantNumeric = 'tabular-nums';
            }
          } else if (field === 'indicator_rsi14') {
            // RSI has color thresholds: >70 overbought (orange), >55 bullish (green), <45 bearish (red)
            const n = Number(value);
            if (value === '' || value === null || value === undefined || isNaN(n)) {
              formattedValue = '-';
            } else {
              formattedValue = n.toFixed(2);
              if (valueDisplay) {
                valueDisplay.style.fontVariantNumeric = 'tabular-nums';
                valueDisplay.style.fontWeight = '600';
                if (n >= 70)      valueDisplay.style.color = '#f59e0b';    // overbought
                else if (n >= 55) valueDisplay.style.color = '#16a34a';    // bullish
                else if (n >= 45) valueDisplay.style.color = '#94a3b8';    // neutral
                else              valueDisplay.style.color = '#dc2626';    // bearish
              }
            }
          } else if (field === 'atr_signal') {
            const s = String(value || '').trim();
            formattedValue = s || '-';
            if (valueDisplay) {
              valueDisplay.style.fontWeight = '600';
              // Color-code by status word
              if (s.startsWith('STOP FIRED') || s.startsWith('STOP WOULD') || s.includes('STOP-NEAR')) {
                valueDisplay.style.color = '#dc2626';
              } else if (s.includes('PARTIAL')) {
                valueDisplay.style.color = '#f59e0b';
              } else if (s.startsWith('TRAIL')) {
                valueDisplay.style.color = '#16a34a';
              } else if (s.startsWith('BUY') || s.startsWith('REENTRY')) {
                valueDisplay.style.color = '#0ea5e9';
              } else if (s === 'DISABLED' || s === 'FLAT' || s.startsWith('WAIT')) {
                valueDisplay.style.color = '#94a3b8';
              } else {
                valueDisplay.style.color = '';
              }
            }
          } else if (field === 'dist_from_stop_pct') {
            // % distance from trailing stop — positive = safe buffer, negative = breached.
            // Five-tier visual: deep green (>1%), green (.5-1%), amber (.2-.5%),
            // orange (0-.2% imminent), red (<0 breached). Padded pill w/ bg for scan-ability.
            if (value === '' || value === null || value === undefined) {
              formattedValue = '-';
            } else {
              const n = Number(value);
              if (isNaN(n)) {
                formattedValue = '-';
              } else {
                formattedValue = (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
                if (valueDisplay) {
                  let fg: string;
                  let bg: string;
                  if (n >= 1.0) {
                    fg = '#ffffff'; bg = '#15803d';      // deep green — comfortable
                  } else if (n >= 0.5) {
                    fg = '#ffffff'; bg = '#16a34a';      // green — safe
                  } else if (n >= 0.2) {
                    fg = '#422006'; bg = '#fbbf24';      // amber — watch
                  } else if (n >= 0) {
                    fg = '#ffffff'; bg = '#ea580c';      // orange — imminent
                  } else {
                    fg = '#ffffff'; bg = '#dc2626';      // red — breached, stop firing
                  }
                  valueDisplay.style.color = fg;
                  valueDisplay.style.background = bg;
                  valueDisplay.style.padding = '1px 6px';
                  valueDisplay.style.borderRadius = '3px';
                  valueDisplay.style.fontWeight = '700';
                  valueDisplay.style.fontVariantNumeric = 'tabular-nums';
                }
              }
            }
          } else if (field === 'order_id_extraction') {
            // Handle order ID extraction field (shows order IDs from ORDER_ID_EXTRACTOR)
            const strValue = String(value);
            if (strValue === '' || strValue === 'null' || strValue === 'undefined' || strValue === '-') {
              formattedValue = '-'; // No order ID yet
            } else {
              // Show the order ID directly
              formattedValue = strValue;
            }
          } else if (field === 'position_size') {
            // Handle position size field - always integers (no fractional shares)
            const numValue = Number(value);
            if (numValue === 0) {
              formattedValue = '-'; // No position
            } else if (isNaN(numValue)) {
              formattedValue = 'N/A';
            } else {
              // Show as integer (no decimals for share quantities)
              formattedValue = Math.round(numValue).toString();
            }
          } else if (field === 'order_status') {
            // Handle order status field (from LIVE_ORDER_STATUS function)
            const strValue = String(value);
            if (strValue === '' || strValue === 'null' || strValue === 'undefined') {
              formattedValue = 'pending'; // Default status
            } else {
              // Show status with appropriate styling
              formattedValue = strValue;
              // Add CSS class for status styling
              if (valueDisplay.parentElement) {
                valueDisplay.parentElement.setAttribute('data-status', strValue);
              }
            }
          } else if (typeof value === 'number') {
            // Handle numeric fields (market data)
          if (field === 'v' || field.includes('q')) {
            // For volume/quantity, show 0 as "-" to indicate no data
            if (value === 0 || isNaN(value)) {
              formattedValue = '-';
            } else if (value >= 10000000) {
              formattedValue = (value / 10000000).toFixed(1) + 'Cr';
            } else if (value >= 100000) {
              formattedValue = (value / 100000).toFixed(1) + 'L';
            } else if (value >= 1000) {
              formattedValue = (value / 1000).toFixed(1) + 'K';
            } else {
              formattedValue = Math.round(value).toString();
            }
          } else if (field === 'pcp') {
            // For percentage, show with + or - sign and %
            const sign = value >= 0 ? '+' : '';
            formattedValue = isNaN(value) ? 'N/A' : sign + value.toFixed(2) + '%';
          } else if (field === 'pc') {
            // For price change, show with + or - sign
            const sign = value >= 0 ? '+' : '';
            if (value === 0) {
              formattedValue = '0.00';
            } else if (isNaN(value)) {
              formattedValue = 'N/A';
            } else {
              formattedValue = sign + value.toFixed(2);
            }
          } else {
            // For prices, show 0 as "-" to indicate no data available
            if (value === 0) {
              formattedValue = '-';
            } else if (isNaN(value)) {
              formattedValue = 'N/A';
            } else {
              formattedValue = value.toFixed(2);
            }
            }
          } else {
            // Handle string fields (fallback)
            formattedValue = String(value);
          }
          
          // Only update if the value has changed
          if (valueDisplay.textContent !== formattedValue) {
            valueDisplay.textContent = formattedValue;
            overlay.classList.remove('loading');
            overlay.classList.add('updating');
            
            // Make overlay visible now that it has data (even if it's zero/dash)
            overlay.style.visibility = 'visible';
            
            setTimeout(() => {
              overlay.classList.remove('updating');
            }, 200);
            
            updatedCount++;
          }
        }
      }
    });
    
    if (updatedCount > 0) {
      console.log(`✅ Updated ${updatedCount} overlays for ${symbolUpper}:${field} = ${value}`);
    }
  }

  private _extractFormulaInfo(text: string): {symbol: string, exchange: string, field: string}|null {
    // Handle cells that show "NoData" or "Loading..." by looking for the formula in the HTML
    let textToSearch = text;
    
    // CRITICAL FIX: Handle truncated ORDER_ functions and placeholders by checking cell attributes
    // If text contains truncated ORDER_ functions or is a placeholder, try to find the full formula
    if ((text.includes('ORDER_') && text.includes('...')) || text === '-' || text === 'NoData' || text.includes('Loading')) {
      const cells = document.querySelectorAll('.field_clip');
      for (const cell of cells) {
        if (cell.textContent === text) {
          const cellElement = cell as HTMLElement;
          const title = cellElement.title || cellElement.getAttribute('title') || '';
          const dataFormula = cellElement.getAttribute('data-formula') || '';
          
          // Check title and data attributes for the full formula (ORDER_, TRAILING_STOP_PRICE, etc.)
          if (title.includes('ORDER_') || dataFormula.includes('ORDER_') ||
              title.includes('TRAILING_STOP_PRICE') || dataFormula.includes('TRAILING_STOP_PRICE') ||
              title.includes('DIST_FROM_STOP') || dataFormula.includes('DIST_FROM_STOP') ||
              title.includes('GET_POSITION_SIZE') || dataFormula.includes('GET_POSITION_SIZE')) {
            textToSearch = title || dataFormula;
            console.log(`🔍 Found full formula in attributes: ${textToSearch}`);
            break;
          }
        }
      }
    }
    
    // If cell shows "NoData", "Loading...", or contains error indicators, try to find the formula in the DOM element
    if (text.includes('NoData') || text.includes('Loading...') || text.includes('#') || text.includes('Error')) {
      // Try to find the formula in the cell HTML or title attributes
      const cells = document.querySelectorAll('.field_clip');
      for (const cell of cells) {
        if (cell.textContent === text) {
          // Check if this cell has a formula in its title or data attributes
          const cellElement = cell as HTMLElement;
          const title = cellElement.title || cellElement.getAttribute('title') || '';
          const dataFormula = cellElement.getAttribute('data-formula') || '';
          
          if (title.includes('LIVE_') || dataFormula.includes('LIVE_')) {
            textToSearch = title || dataFormula;
            console.log(`🔍 Found formula in title/data: ${textToSearch}`);
            break;
          }
          
          // Also check child elements for formula content (LIVE_ and ORDER_ functions)
          const formulaElements = cellElement.querySelectorAll('[title*="LIVE_"], [data-formula*="LIVE_"], [title*="ORDER_"], [data-formula*="ORDER_"], [title*="GET_POSITION_SIZE"], [data-formula*="GET_POSITION_SIZE"], [title*="GET_TSL_ORDER_STATUS"], [data-formula*="GET_TSL_ORDER_STATUS"], [title*="ORDER_ID_EXTRACTOR"], [data-formula*="ORDER_ID_EXTRACTOR"], [title*="TRAILING_STOP_PRICE"], [data-formula*="TRAILING_STOP_PRICE"], [title*="FILLED_QTY"], [title*="AVG_PRICE"], [title*="TRIGGER_PRICE"], [title*="LAST_FILL_TIME"]');
          if (formulaElements.length > 0) {
            textToSearch = (formulaElements[0] as HTMLElement).title || 
                          (formulaElements[0] as HTMLElement).getAttribute('data-formula') || '';
            console.log(`🔍 Found formula in child element: ${textToSearch}`);
            break;
          }
        }
      }
    }
    
    // Matches LIVE_PRICE("SYM","EX") or LIVE_VOLUME($Symbol,$Exchange) or LIVE_CHANGE("SYMBOL","NSE")
    const liveMatch = textToSearch.match(/LIVE_([A-Z_0-9]+)\s*\(\s*["']?([^"',\s$]+)["']?\s*,\s*["']?([^"',\s$]+)["']?\s*\)/i);
    if (liveMatch) {
      const [, func, symbol, exchange] = liveMatch;
      
      // Use the centralized field mapping
      const field = this._mapFunctionToField(`LIVE_${func}`);
      
      if (!field) {
        console.warn(`❌ Unknown LIVE_${func} function - no server field mapping`);
        return null;
      }
      
      return {symbol: symbol.toUpperCase(), exchange: exchange.toUpperCase(), field};
    }
    
    // Match trailing stop overlay function TRAILING_STOP_PRICE
    const trailingOverlayMatch = textToSearch.match(/TRAILING_STOP_PRICE\s*\(\s*["']?\$?([^"',\s]+)["']?\s*,\s*([^,\s)]+)\s*,\s*["']?\$?([^"',\s]+)["']?\s*\)/i);
    if (trailingOverlayMatch) {
      const [, symbol, , exchange] = trailingOverlayMatch;
      const field = 'trailing_stop_price';
      return {symbol: symbol.toUpperCase(), exchange: exchange.toUpperCase(), field};
    }

    // Match DIST_FROM_STOP(symbol, exchange) — live % distance from trailing stop
    const distFromStopMatch = textToSearch.match(/DIST_FROM_STOP\s*\(\s*["']?\$?([^"',\s)]+)["']?\s*(?:,\s*["']?\$?([^"',\s)]+)["']?\s*)?\)/i);
    if (distFromStopMatch) {
      const [, symbol, exchange] = distFromStopMatch;
      return {
        symbol: symbol.toUpperCase(),
        exchange: (exchange || 'NSE').toUpperCase(),
        field: 'dist_from_stop_pct',
      };
    }

    // Match LIVE_PNL("symbol", "exchange", avg, qty) — live unrealized P&L.
    // Args 3 & 4 are optional — server-side Python auto-resolves from broker
    // sync when missing, and embeds them in the returned placeholder so the
    // tick handler can compute (ltp - avg) * qty client-side.
    const livePnlMatch = textToSearch.match(/LIVE_PNL\s*\(\s*["']?\$?([^"',\s)]+)["']?\s*,\s*["']?\$?([^"',\s)]+)["']?\s*(?:,\s*([\-0-9.]+)\s*,\s*([\-0-9.]+))?\s*\)/i);
    if (livePnlMatch) {
      const [, symbol, exchange, avgStr, qtyStr] = livePnlMatch;
      return {
        symbol: symbol.toUpperCase(),
        exchange: (exchange || 'NSE').toUpperCase(),
        field: 'pnl_realtime',
        avgPrice: avgStr ? (parseFloat(avgStr) || 0) : 0,
        quantity: qtyStr ? (parseFloat(qtyStr) || 0) : 0,
      } as any;
    }

    // ATR strategy overlay fields — 4 state columns + 3 live indicator columns.
    // Each gets a live push from server on every tick via _directATRUpdate.
    const atrFieldMap: Record<string, string> = {
      'ATR_STRATEGY_SIGNAL': 'atr_signal',
      'ATR_STOP_PRICE': 'atr_stop_price',
      'ATR_HWM': 'atr_hwm',
      'ATR_DIST_PCT': 'atr_dist_pct',
      // LIVE indicator columns — recomputed from daily OHLC + today's live bar each tick.
      // LIVE_EMA default period is 20 → `indicator_ema20`; LIVE_RSI default 14 → `indicator_rsi14`;
      // LIVE_ATR default 14 → `indicator_atr14`. If users pass non-default periods the overlay
      // will still land on the default field (acceptable — Python still returns the period-specific value).
      'LIVE_EMA': 'indicator_ema20',
      'LIVE_RSI': 'indicator_rsi14',
      'LIVE_ATR': 'indicator_atr14',
    };
    for (const fn of Object.keys(atrFieldMap)) {
      const m = textToSearch.match(new RegExp(
        fn + '\\s*\\(\\s*["\']?\\$?([^"\',\\s)]+)["\']?\\s*(?:,\\s*["\']?\\$?([^"\',\\s)]+)["\']?\\s*)?',
        'i'
      ));
      if (m) {
        return {
          symbol: m[1].toUpperCase(),
          exchange: (m[2] || 'NSE').toUpperCase(),
          field: atrFieldMap[fn],
        };
      }
    }
    
    // NEW: Match trading action functions PLACE_ORDER, CANCEL_ORDER, TRAIL
    const tradingActionMatch = textToSearch.match(/(PLACE_ORDER|CANCEL_ORDER|TRAIL)\s*\(\s*["']?([^"',\s$]+)["']?\s*,\s*["']?([^"',\s$]+)["']?(?:\s*,\s*[^)]+)?\s*\)/i);
    if (tradingActionMatch) {
      const [, func, symbol, exchange] = tradingActionMatch;
      console.log(`🔍 Trading action match: ${func}("${symbol}", "${exchange}")`);
      
      // Use the centralized field mapping
      const field = this._mapFunctionToField(func);
      
      if (!field) {
        console.warn(`❌ Unknown ${func} function - no server field mapping`);
        return null;
      }
      
      console.log(`✅ Trading action mapped: ${func} -> ${field}`);
      return {symbol: symbol.toUpperCase(), exchange: exchange.toUpperCase(), field};
    }
    
    // Match trailing stop functions TRAILING_PRICE, TRAILING_PCT, TRAILING_STOP_PRICE
    const trailingMatch = textToSearch.match(/(TRAILING_PRICE|TRAILING_PCT|TRAILING_STOP_PRICE)\s*\(\s*["']?([^"',\s$]+)["']?\s*\)/i);
    if (trailingMatch) {
      const [, func, orderId] = trailingMatch;
      console.log(`🔍 Trailing function match: ${func}("${orderId}")`);
      
      // For trailing functions, symbol is derived from orderId
      // Use orderId as symbol for now (could be enhanced to lookup actual symbol)
      const field = this._mapFunctionToField(func);
      
      if (!field) {
        console.warn(`❌ Unknown ${func} function - no server field mapping`);
        return null;
      }
      
      console.log(`✅ Trailing function mapped: ${func} -> ${field}`);
      return {symbol: orderId.toUpperCase(), exchange: 'NSE', field};
    }
    
    // ENHANCED: Match ORDER_ functions with comprehensive regex that includes all ORDER-related functions
    const orderMatch = textToSearch.match(/(ORDER_STATUS|ORDER_TYPE|ORDER_QUANTITY|ORDER_PRICE|ORDER_PRODUCT|ORDER_VALIDITY|FILLED_QTY|TRIGGER_PRICE|LAST_FILL_TIME|PRODUCT_TYPE|AVG_PRICE|LIVE_ORDER_STATUS)\s*\(\s*["']?([^"',\s$]+)["']?\s*(?:,\s*["']?([^"',\s$]+)["']?)?\s*\)/i);
    if (orderMatch) {
      const [, func, firstParam, secondParam] = orderMatch;
      console.log(`🔍 ORDER function match: ${func}("${firstParam}", "${secondParam || 'N/A'}")`);
      
      // For LIVE_ORDER_STATUS, the first parameter is order ID, not symbol
      if (func === 'LIVE_ORDER_STATUS') {
        const orderId = firstParam;
        const field = 'order_status';
        
        console.log(`✅ LIVE_ORDER_STATUS mapped: orderId=${orderId} -> field=${field}`);
        // Use order ID as symbol and special exchange marker
        return {symbol: orderId.toUpperCase(), exchange: 'ORDER_STATUS', field};
      }
      
      // For other ORDER functions, use normal symbol/exchange logic
      const symbol = firstParam;
      const exchange = secondParam;
      
      // Use the centralized field mapping
      const field = this._mapFunctionToField(func);
      
      if (!field) {
        console.warn(`❌ Unknown ${func} function - no server field mapping`);
        return null;
      }
      
      console.log(`✅ ORDER function mapped: ${func} -> ${field}`);
      // Use 'ANY' for exchange when not specified, allowing lookup from any exchange
      return {symbol: symbol.toUpperCase(), exchange: (exchange || 'ANY').toUpperCase(), field};
    }
    
    // ENHANCED: Try to match truncated ORDER_ functions by looking for common patterns
    const truncatedOrderMatch = textToSearch.match(/ORDER_([A-Z_]{2,})\.\.\./);
    if (truncatedOrderMatch) {
      const [, partialFunc] = truncatedOrderMatch;
      console.log(`🔍 Truncated ORDER_ function detected: ORDER_${partialFunc}...`);
      
      // Try to infer the full function name from the partial match
      const possibleFunctions = [
        'ORDER_TYPE', 'ORDER_STATUS', 'ORDER_QUANTITY', 'ORDER_PRICE',
        'ORDER_PRODUCT', 'ORDER_VALIDITY', 'FILLED_QTY', 'AVG_PRICE',
        'TRIGGER_PRICE', 'LAST_FILL_TIME'
      ];
      
      const matchedFunction = possibleFunctions.find(func => func.startsWith(`ORDER_${partialFunc}`));
      if (matchedFunction) {
        console.log(`🔍 Inferred function: ${matchedFunction}`);
        
        // For truncated functions, we need to extract symbol from the cell context
        // This is a fallback approach - try to find the symbol from the row context
        const cell = document.querySelector('.field_clip')?.closest('tr')?.querySelector('.field_clip');
        if (cell) {
          const symbolCell = cell.textContent || '';
          const symbolMatch = symbolCell.match(/([A-Z0-9]+)/);
          if (symbolMatch) {
            const symbol = symbolMatch[1];
            const field = this._mapFunctionToField(matchedFunction);
            
            if (field) {
              console.log(`✅ Truncated ORDER function mapped: ${matchedFunction} -> ${field} for symbol ${symbol}`);
              return {symbol: symbol.toUpperCase(), exchange: 'NSE', field};
            }
          }
        }
      }
    }
    
      // Also try to match simpler patterns like LIVE_PRICE(SYMBOL,NSE)
      const simpleMatch = textToSearch.match(/LIVE_([A-Z_0-9]+)\s*\(\s*([A-Z0-9_-]+)\s*,\s*([A-Z]+)\s*\)/i);
      if (simpleMatch) {
        const [, func, symbol, exchange] = simpleMatch;
        console.log(`✅ Found simple pattern: LIVE_${func}(${symbol},${exchange})`);
        const field = this._mapFunctionToField(`LIVE_${func}`);
        if (field) {
          return {symbol: symbol.toUpperCase(), exchange: exchange.toUpperCase(), field};
        }
      }
    
      // Match position size overlay function GET_POSITION_SIZE("SYM","EX")
      const positionSizeMatch = textToSearch.match(/GET_POSITION_SIZE\s*\(\s*["']?\$?([^"',\s]+)["']?\s*,\s*["']?\$?([^"',\s]+)["']?\s*\)/i);
      if (positionSizeMatch) {
        const [, symbol, exchange] = positionSizeMatch;
        const field = this._mapFunctionToField('GET_POSITION_SIZE');
        if (!field) {
          console.warn(`❌ Unknown GET_POSITION_SIZE function - no server field mapping`);
          return null;
        }
        return {symbol: symbol.toUpperCase(), exchange: exchange.toUpperCase(), field};
      }

      // Match TSL order status overlay function GET_TSL_ORDER_STATUS("SYM","EX")
      const tslOrderStatusMatch = textToSearch.match(/GET_TSL_ORDER_STATUS\s*\(\s*["']?\$?([^"',\s]+)["']?\s*,\s*["']?\$?([^"',\s]+)["']?\s*\)/i);
      if (tslOrderStatusMatch) {
        const [, symbol, exchange] = tslOrderStatusMatch;
        const field = this._mapFunctionToField('GET_TSL_ORDER_STATUS');
        if (!field) {
          console.warn(`❌ Unknown GET_TSL_ORDER_STATUS function - no server field mapping`);
          return null;
        }
        return {symbol: symbol.toUpperCase(), exchange: exchange.toUpperCase(), field};
      }

      // Match ORDER_ID_EXTRACTOR overlay function ORDER_ID_EXTRACTOR($trailing_stop_loss)
      const orderIdExtractorMatch = textToSearch.match(/ORDER_ID_EXTRACTOR\s*\(\s*\$?([^"',\s)]+)\s*\)/i);
      if (orderIdExtractorMatch) {
        const [, inputParam] = orderIdExtractorMatch;
        const field = this._mapFunctionToField('ORDER_ID_EXTRACTOR');
        if (!field) {
          console.warn(`❌ Unknown ORDER_ID_EXTRACTOR function - no server field mapping`);
          return null;
        }
        // Use the input parameter as the symbol for tracking
        return {symbol: inputParam.toUpperCase(), exchange: 'NSE', field};
      }
    
      // Universal fallback: if the cell has formula in title/data attributes, prefer that.
      {
        const cells = document.querySelectorAll('.field_clip');
        for (const cell of cells) {
          if (cell.textContent === text) {
            const cellElement = cell as HTMLElement;
            const titleAttr = cellElement.getAttribute('title') || '';
            const dataFormulaAttr = cellElement.getAttribute('data-formula') || '';
            if (titleAttr.match(/GET_POSITION_SIZE|GET_TSL_ORDER_STATUS|ORDER_ID_EXTRACTOR|TRAILING_STOP_PRICE|LIVE_|ORDER_/i) ||
                dataFormulaAttr.match(/GET_POSITION_SIZE|GET_TSL_ORDER_STATUS|ORDER_ID_EXTRACTOR|TRAILING_STOP_PRICE|LIVE_|ORDER_/i)) {
              textToSearch = titleAttr || dataFormulaAttr;
              // Re-run extraction with updated text
              return this._extractFormulaInfo(textToSearch);
            }
          }
        }
      }
    
      return null;
  }

  private _mapFunctionToField(func: string): string | null {
    const functionFieldMap: Record<string, string> = {
      // Core price/volume - Fixed to match Redis field names
      'LIVE_PRICE': 'ltp',
      'LIVE_VOLUME': 'v',
      'LIVE_CHANGE': 'pc',
      'LIVE_CHANGE_PERCENT': 'pcp',
      
      // OHLC data - Available from Shoonya depth acknowledgment messages
      'LIVE_HIGH': 'h',
      'LIVE_LOW': 'l',
      'LIVE_OPEN': 'o',
      'LIVE_CLOSE': 'c',
      
      // Level 1 Bid/Ask (best) - Fixed to match Redis field names
      'LIVE_BID_PRICE': 'bp1',
      'LIVE_BID_QTY': 'bq1',
      'LIVE_ASK_PRICE': 'sp1',
      'LIVE_ASK_QTY': 'sq1',
      'LIVE_BID_PRICE_L1': 'bp1',
      'LIVE_BID_QTY_L1': 'bq1',
      'LIVE_ASK_PRICE_L1': 'sp1',
      'LIVE_ASK_QTY_L1': 'sq1',
      
      // Level 2 Bid/Ask - Fixed to match Redis field names
      'LIVE_BID_PRICE_L2': 'bp2',
      'LIVE_BID_QTY_L2': 'bq2',
      'LIVE_ASK_PRICE_L2': 'sp2',
      'LIVE_ASK_QTY_L2': 'sq2',
      
      // Level 3 Bid/Ask - Fixed to match Redis field names
      'LIVE_BID_PRICE_L3': 'bp3',
      'LIVE_BID_QTY_L3': 'bq3',
      'LIVE_ASK_PRICE_L3': 'sp3',
      'LIVE_ASK_QTY_L3': 'sq3',
      
      // Level 4 Bid/Ask - Fixed to match Redis field names
      'LIVE_BID_PRICE_L4': 'bp4',
      'LIVE_BID_QTY_L4': 'bq4',
      'LIVE_ASK_PRICE_L4': 'sp4',
      'LIVE_ASK_QTY_L4': 'sq4',
      
      // Level 5 Bid/Ask - Fixed to match Redis field names
      'LIVE_BID_PRICE_L5': 'bp5',
      'LIVE_BID_QTY_L5': 'bq5',
      'LIVE_ASK_PRICE_L5': 'sp5',
      'LIVE_ASK_QTY_L5': 'sq5',
      
      // Additional fields - Fixed to match Redis field names
      'LIVE_AVERAGE_PRICE': 'ap',
      'LIVE_LAST_TRADE_TIME': 'ltt',
      'LIVE_LAST_TRADE_QTY': 'ltq',
      'LIVE_UPPER_CIRCUIT': 'uc',
      'LIVE_LOWER_CIRCUIT': 'lc',

      // 'LIVE_OPEN_INTEREST': 'oi',  // Not available for equity stocks
      'LIVE_TOTAL_BUY_QTY': 'tbq',
      'LIVE_TOTAL_SELL_QTY': 'tsq',

      // Order fields - handle via overlay system
      'ORDER_STATUS': 'order_status',
      'LIVE_ORDER_STATUS': 'order_status',
      'ORDER_TYPE': 'order_trantype',
      'ORDER_QUANTITY': 'order_qty',
      'FILLED_QTY': 'order_fillshares',
      'AVG_PRICE': 'order_avgprc',
      'ORDER_PRICE': 'order_prc',
      'ORDER_PRODUCT': 'order_pcode',
      'PRODUCT_TYPE': 'order_pcode',
      'ORDER_VALIDITY': 'order_ret',
      'TRIGGER_PRICE': 'order_trgprc',
      'LAST_FILL_TIME': 'order_fltm',
      
      // Trading action status fields
      'PLACE_ORDER': 'place_order_status',
      'CANCEL_ORDER': 'cancel_order_status',
      'TRAIL': 'trail_status',
      
      // Trailing stop fields from Redis pub/sub
      'TRAILING_PRICE': 'trailing_price',
      'TRAILING_PCT': 'trailing_pct',
      
      // Trailing Stop Loss functions
      'TRAILING_STOP_PRICE': 'trailing_stop_price',
      'TRAILING_STOP_STATUS': 'trailing_stop_status',
      'TRAILING_STOP_LOSS': 'trailing_stop_loss',
      'DIST_FROM_STOP': 'dist_from_stop_pct',
      'LIVE_PNL': 'pnl_realtime',
      // ATR strategy overlay fields (server-computed on every tick)
      'ATR_STRATEGY_SIGNAL': 'atr_signal',
      'ATR_STOP_PRICE': 'atr_stop_price',
      'ATR_HWM': 'atr_hwm',
      'ATR_DIST_PCT': 'atr_dist_pct',
      // Live indicators (server computes from daily OHLC + today's live bar every tick)
      'LIVE_EMA': 'indicator_ema20',
      'LIVE_RSI': 'indicator_rsi14',
      'LIVE_ATR': 'indicator_atr14',
      'GET_POSITION_SIZE': 'position_size',
      'GET_TSL_ORDER_STATUS': 'tsl_order_status',
      'ORDER_ID_EXTRACTOR': 'order_id_extraction',
      
      // NEW: Advanced trading formulas
      'LIVE_BID_ASK_SPREAD': 'bid_ask_spread',
      'LIVE_BID_ASK_SPREAD_PERCENT': 'bid_ask_spread_percent',
      'LIVE_MID_PRICE': 'mid_price',
      'LIVE_VOLUME_WEIGHTED_PRICE': 'vwap',
      'LIVE_MARKET_DEPTH_IMBALANCE': 'market_depth_imbalance',
      'LIVE_PRICE_IMPACT': 'price_impact',
      'LIVE_VOLATILITY_ESTIMATE': 'volatility_estimate',
      'LIVE_MOMENTUM_INDICATOR': 'momentum_indicator',
    };
    
    const result = functionFieldMap[func] || null;
    if (!result) {
      console.warn(`❌ No mapping found for function: ${func}`);
    } else {
      console.log(`✅ Mapped function ${func} to field: ${result}`);
    }
    
    return result;
  }

  private _injectTSLStatusStyles(): void {
    const styleId = 'tsl-status-styles';
    
    // Check if styles already exist
    if (document.getElementById(styleId)) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* TSL Order Status Overlay Styles */
      .live-overlay[data-status="completed"] .live-price-value {
        background-color: rgba(40, 167, 69, 0.9) !important;
        color: white !important;
        font-weight: bold;
      }
      
      .live-overlay[data-status="pending"] .live-price-value {
        background-color: rgba(255, 193, 7, 0.9) !important;
        color: #212529 !important;
        font-weight: bold;
      }
      
      .live-overlay[data-status="rejected"] .live-price-value {
        background-color: rgba(220, 53, 69, 0.9) !important;
        color: white !important;
        font-weight: bold;
      }
      
      .live-overlay[data-status="cancelled"] .live-price-value {
        background-color: rgba(108, 117, 125, 0.9) !important;
        color: white !important;
        font-weight: bold;
      }
      
      /* Animation for TSL status changes */
      .live-overlay[data-field="tsl_order_status"] .live-price-value {
        transition: all 0.3s ease;
      }
    `;
    
    document.head.appendChild(style);
    console.log('✅ TSL order status styles injected');
  }

  // NEW: remove overlays whose parent cell no longer contains a LIVE_PRICE formula
  private _cleanupOrphanedOverlays(): void {
    this._overlayMap.forEach((storedKey, overlay) => {
      const parent = overlay.parentElement as HTMLElement | null;
      if (!parent) {
        this._overlayMap.delete(overlay);
        return;
      }
      const cellFormula = this._extractFormulaInfo(parent.textContent || '');
      if (!cellFormula) {
        // Formula removed entirely
        this._removeOverlay(overlay);
        return;
      }
      const currentKey = `${cellFormula.symbol}-${cellFormula.exchange}`;
      if (currentKey !== storedKey) {
        // Symbol changed (recycled cell) – remove old overlay and schedule new scan
        this._removeOverlay(overlay);
        // schedule quick rescan for this cell
        setTimeout(() => this._scanForNewFormulas(), 50);
      }
    });
  }

  private _setupScrollListener(): void {
    const handleScroll = () => this._scheduleHotZoneUpdate();

    // Primary window scroll (rare for grids but keep)
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Attach to all existing grid scroller elements
    document.querySelectorAll('.gridview_data_area, .gridview_scroller, .grid_body').forEach(el => {
      el.addEventListener('scroll', handleScroll, { passive: true });
    });

    // Run once in case grid programmatically scrolled without event
    this._scheduleHotZoneUpdate();

    // Observe DOM for any new grid scroller that may appear (e.g. after view change)
    const scrollerObserver = new MutationObserver(muts => {
      muts.forEach(m => {
        m.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches('.gridview_data_area, .gridview_scroller, .grid_body')) {
            node.addEventListener('scroll', handleScroll, { passive: true });
          }
        });
      });
    });
    const gridContainer = document.querySelector('.gridview_data_area') || document.body;
    scrollerObserver.observe(gridContainer, { childList: true, subtree: true });
  }


  /**
   * Set up detection for when Grist rebuilds table DOM (which removes all overlays)
   */
  private _setupDOMRebuildDetection(): void {
    // Monitor for when table containers are rebuilt by Grist during page navigation
    const observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;
      
      for (const mutation of mutations) {
        // Check if major DOM restructuring happened (like table rebuild)
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Look for table containers or grid views being added
              if (element.classList?.contains('viewsection_content') || 
                  element.classList?.contains('gridview_data_pane') ||
                  element.querySelector?.('.viewsection_content, .gridview_data_pane')) {
                console.log('🔄 DOM rebuild detected - table content recreated');
                shouldReinitialize = true;
                break;
              }
            }
          }
        }
        if (shouldReinitialize) break;
      }
      
      if (shouldReinitialize) {
        // Clear tracking of old overlays since DOM was rebuilt
        this._overlayMap.clear();
        this._symbolToOverlays.clear();
        
        // Reinitialize overlays for the new DOM
        setTimeout(() => {
          console.log('🚀 Reinitializing overlays after DOM rebuild');
          this._initializeInstantOverlays();
        }, 100);
      }
    });
    
    // Observe the main document for major DOM changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    this.autoDispose({ dispose: () => observer.disconnect() });
  }

  
    // Schedules an update of hot zone using requestAnimationFrame for minimal latency
  private _scheduleHotZoneUpdate(): void {
    if (this._hotZoneScheduled) return;
    this._hotZoneScheduled = true;
    window.requestAnimationFrame(() => {
      this._hotZoneScheduled = false;
      this._updateHotZone();
    });
  }

  // Update overlays within a buffered hot zone around viewport
  private _updateHotZone(): void {
    const bufferPx = 1200;
    const viewportTop = -bufferPx;
    const viewportBottom = window.innerHeight + bufferPx;

    // Remove overlays outside hot zone
    this._overlayMap.forEach((symbolKey, overlay) => {
      const rect = overlay.parentElement?.getBoundingClientRect();
      if (!rect) return;
      if (rect.bottom < viewportTop || rect.top > viewportBottom) {
        this._removeOverlay(overlay);
      }
    });

    // Collect symbols that need prefetch
    const symbolsToPrefetch = new Set<string>();

    // Scan cells for missing overlays
    const cells = document.querySelectorAll('.field_clip');
    
          cells.forEach(cell => {
        const rect = (cell as HTMLElement).getBoundingClientRect();
        if (rect.bottom < viewportTop || rect.top > viewportBottom) return;
        
        if (cell.getAttribute('data-has-overlay') === 'true') return;

        // First try to extract formula from cell text
        let formulaInfo = this._extractFormulaInfo(cell.textContent || '');
        
        // If no formula found and cell shows placeholder, check title and data attributes directly
        if (!formulaInfo && (cell.textContent === '-' || cell.textContent === 'NoData')) {
          const cellElement = cell as HTMLElement;
          const title = cellElement.title || cellElement.getAttribute('title') || '';
          const dataFormula = cellElement.getAttribute('data-formula') || '';
          
          if (title || dataFormula) {
            formulaInfo = this._extractFormulaInfo(title || dataFormula);
            if (formulaInfo) {
              console.log(`🔍 Extracted formula from placeholder cell attributes: ${title || dataFormula}`);
            }
          }
        }
        
        if (formulaInfo) {
          this._createDirectOverlay(cell as HTMLElement, formulaInfo.symbol, formulaInfo.exchange, formulaInfo.field);
          const key = `${formulaInfo.symbol}-${formulaInfo.exchange}`;
          if (!this._priceCache.has(key)) {
            symbolsToPrefetch.add(formulaInfo.symbol.toUpperCase());
            if (!this._pendingFetch.has(key)) {
              this._pendingFetch.add(key);
            }
          }
        }
      });

    if (symbolsToPrefetch.size > 0) {
      this._prefetchPrices(Array.from(symbolsToPrefetch));
    }
  }

  // Helper to remove overlay and manage bookkeeping
  private _removeOverlay(overlay: HTMLElement): void {
    const symbolKey = this._overlayMap.get(overlay);
    if (!symbolKey) return;
    // symbolKey format is "SYMBOL-EXCHANGE" (e.g. "HAVELLS-NSE")
    const parts = symbolKey.split('-');
    const symbolUpper = parts[0].toUpperCase();
    const exchange = (parts[1] || 'NSE').toUpperCase();

    // Remove from reverse symbol lookup
    const overlaySet = this._symbolToOverlays.get(symbolUpper);
    if (overlaySet) {
      overlaySet.delete(overlay);
      if (overlaySet.size === 0) {
        this._symbolToOverlays.delete(symbolUpper);
      }
    }

    // Decrement refcount — keyed by EXCHANGE:SYMBOL to match subscribe
    const exchangeTag = `${exchange}:${symbolUpper}`;
    const count = (this._symbolRefCount.get(exchangeTag) || 1) - 1;
    if (count <= 0) {
      this._symbolRefCount.delete(exchangeTag);
      this._subscribedSymbols.delete(exchangeTag);
      this._overlayComm.unsubscribe([exchangeTag]);
    } else {
      this._symbolRefCount.set(exchangeTag, count);
    }
    const parent = overlay.parentElement as HTMLElement | null;
    overlay.remove();
    if (parent) {
      parent.removeAttribute('data-has-overlay');
      parent.removeAttribute('data-symbol');
      parent.removeAttribute('data-exchange');
      parent.removeAttribute('data-field'); // Also remove field attribute
    }
    this._overlayMap.delete(overlay);
  }



  // Fetch price for a single symbol/exchange when first needed
    private async _fetchSinglePrice(symbol: string, exchange: string): Promise<void> {
    try {
      // Try multiple symbol formats to handle -EQ suffix
      const symbolsToTry = [
        symbol,
        `${symbol}-EQ`,
        symbol.replace('-EQ', ''),
        symbol.split('-')[0] // Remove any suffix
      ];
      
      // Use the new fields API for single symbol fetch
      const resp = await fetch('/api/live-fields/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols: symbolsToTry }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      
      // ✅ RESPECT SPECIFIC EXCHANGE: If exchange is specified, use ONLY that exchange
      let fieldData = null;
      let actualSymbol = symbol;
      
      console.log(`🔍 _fetchSinglePrice called with symbol="${symbol}", exchange="${exchange}"`);
      console.log(`📊 Available data keys:`, Object.keys(data));
      
      if (exchange && exchange !== 'AUTO') {
        // SPECIFIC EXCHANGE REQUESTED - only use data from that exchange
        console.log(`🎯 Looking for ${symbol} specifically on ${exchange} exchange`);
        
        for (const [responseKey, responseData] of Object.entries(data)) {
          if (responseData && (responseData as any).ltp !== undefined) {
            const dataSymbol = (responseData as any).symbol;
            const keyExchange = responseKey.includes('NSE') ? 'NSE' : 
                             responseKey.includes('BSE') ? 'BSE' : 'UNKNOWN';
                             
            // Only consider data from the requested exchange
            if (keyExchange !== exchange.toUpperCase()) {
              continue;
            }
            
            // Check multiple symbol variations for matching
            const cleanOriginal = symbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
            const cleanActual = dataSymbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
            
            if (cleanOriginal === cleanActual ||
                dataSymbol === symbol || 
                dataSymbol === `${symbol}-EQ` ||
                dataSymbol.replace('-EQ', '') === symbol ||
                symbol.replace('-EQ', '') === dataSymbol.replace('-EQ', '') ||
                responseKey.includes(cleanOriginal) ||
                responseKey.includes(symbol)) {
              
              fieldData = responseData;
              actualSymbol = dataSymbol;
              console.log(`✅ Found ${symbol} data on requested ${exchange} exchange: ${actualSymbol}`);
              break;
            }
          }
        }
        
        if (!fieldData) {
          console.warn(`❌ No data found for ${symbol} on ${exchange} exchange`);
          return;
        }
        
      } else {
        // AUTO MODE - use exchange preference (NSE > BSE > others) for backward compatibility  
        let bestMatch = null;
        let bestPriority = -1;
        
        const getExchangePriority = (key: string, dataSymbol: string): number => {
          if (key.includes('NSE') || dataSymbol.includes('-EQ') || dataSymbol.includes('NSE')) return 3;
          if (key.includes('BSE') || (!dataSymbol.includes('-') && !key.includes('NSE'))) return 2;
          return 1;
        };
        
        for (const [responseKey, responseData] of Object.entries(data)) {
          if (responseData && (responseData as any).ltp !== undefined) {
            const dataSymbol = (responseData as any).symbol;
            
            // Check multiple symbol variations for matching
            const cleanOriginal = symbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
            const cleanActual = dataSymbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
            
            if (cleanOriginal === cleanActual ||
                dataSymbol === symbol || 
                dataSymbol === `${symbol}-EQ` ||
                dataSymbol.replace('-EQ', '') === symbol ||
                symbol.replace('-EQ', '') === dataSymbol.replace('-EQ', '') ||
                responseKey.includes(cleanOriginal) ||
                responseKey.includes(symbol)) {
              
              const priority = getExchangePriority(responseKey, dataSymbol);
              if (priority > bestPriority) {
                bestMatch = { responseKey, responseData, dataSymbol };
                bestPriority = priority;
              }
            }
          }
        }
        
        if (bestMatch) {
          fieldData = bestMatch.responseData;
          actualSymbol = bestMatch.dataSymbol;
          const exchangeName = bestPriority === 3 ? 'NSE' : bestPriority === 2 ? 'BSE' : 'Other';
          console.log(`✅ Found data for ${symbol} using key ${bestMatch.responseKey}, actual symbol: ${actualSymbol} (${exchangeName} preferred)`);
        }
      }
      
      if (!fieldData) {
        console.warn(`❌ No data found for ${symbol} in any format`);
        return;
      }
      
      const key = `${symbol}-${exchange}`;  // Keep using original symbol for cache key
      
      // Process LTP data for existing price overlay system
      if ((fieldData as any).ltp !== undefined) {
        const priceUpdate = {
          symbol,
          exchange,
          price: (fieldData as any).ltp,
          change: (fieldData as any).pc || 0,
          changePercent: (fieldData as any).pcp || 0,
          timestamp: (fieldData as any).timestamp || Date.now(),
        } as any;
        this._priceCache.set(key, priceUpdate);
        this._pendingFetch.delete(key);
        this._updateCellsForSymbol(symbol, exchange, priceUpdate);
      }

      // Process other field data for overlays
      Object.entries(fieldData as any).forEach(([field, value]: [string, any]) => {
        if (field !== 'symbol' && field !== 'exchange' && field !== 'timestamp' && value !== undefined) {
          // Update overlays for this specific field
          this._updateCellsForField(symbol, exchange, field, value);
        }
      });
    } catch (e) {
      console.warn('Failed to fetch initial data for', symbol, e);
    }
  }

    // Batch fetch for multiple symbols
  private async _prefetchPrices(symbols: string[]): Promise<void> {
    try {
      // Try multiple symbol formats for each symbol
      const allSymbolsToTry: string[] = [];
      symbols.forEach(symbol => {
        allSymbolsToTry.push(
          symbol,
          `${symbol}-EQ`,
          symbol.replace('-EQ', ''),
          symbol.split('-')[0]
        );
      });
      
      // Remove duplicates
      const uniqueSymbols = [...new Set(allSymbolsToTry)];
      
      const resp = await fetch('/api/live-fields/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols: uniqueSymbols }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      
      // Create mapping from original symbols to response data using enhanced matching
      const symbolMapping = new Map<string, any>();
      symbols.forEach(originalSymbol => {
        // Find the best matching response
        for (const [responseKey, responseData] of Object.entries(data)) {
          if (responseData && (responseData as any).ltp !== undefined) {
            const actualSymbol = (responseData as any).symbol;
            
            // Check multiple symbol variations for matching
            const cleanOriginal = originalSymbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
            const cleanActual = actualSymbol.replace('-EQ', '').replace('-NSE', '').toUpperCase();
            
            if (cleanOriginal === cleanActual ||
                actualSymbol === originalSymbol || 
                actualSymbol === `${originalSymbol}-EQ` ||
                actualSymbol.replace('-EQ', '') === originalSymbol ||
                originalSymbol.replace('-EQ', '') === actualSymbol.replace('-EQ', '') ||
                responseKey.includes(cleanOriginal) ||
                responseKey.includes(originalSymbol)) {
              symbolMapping.set(originalSymbol, responseData);
              console.log(`🔄 Prefetch mapped ${originalSymbol} to data from ${actualSymbol} (key: ${responseKey})`);
              break;
            }
          }
        }
      });
      
      // Process mapped data
      symbolMapping.forEach((fieldData, originalSymbol) => {
        if (!fieldData) {
          console.warn(`❌ No data found for symbol: ${originalSymbol}`);
          return;
        }
        
        const symbol = originalSymbol.toUpperCase(); // Use original symbol for consistency
        const exchange = fieldData.exchange.toUpperCase();
        
        console.log(`✅ Processing batch data for ${originalSymbol} using mapped data`);
        
        // Process LTP data for existing price overlay system
        if (fieldData.ltp !== undefined) {
          const priceUpdate = {
            symbol,
            exchange,
            price: fieldData.ltp,
            change: fieldData.pc ?? 0,
            changePercent: fieldData.pcp ?? 0,
            timestamp: fieldData.timestamp || Date.now(),
          } as any;
          const cacheKey = `${symbol}-${exchange}`;
          this._priceCache.set(cacheKey, priceUpdate);
          this._pendingFetch.delete(cacheKey);
          this._updateCellsForSymbol(symbol, exchange, priceUpdate);
        }

        // Process other field data for overlays
        Object.entries(fieldData).forEach(([field, value]: [string, any]) => {
          if (field !== 'symbol' && field !== 'exchange' && field !== 'timestamp' && value !== undefined) {
            // Update overlays for this specific field
            this._updateCellsForField(symbol, exchange, field, value);
          }
        });
      });
    } catch (e) {
      console.warn('Batch prefetch failed', e);
    }
  }

  // Retry mechanism for symbols stuck in loading state
  private _startRetryTimer(): void {
    // REAL-TIME ORDER DATA: Make ORDER_ data as fast as LTP data
    console.log('🔄 SimpleLiveOverlay: Starting real-time order data processing');
    
    // Initial order data fetch immediately
    setTimeout(() => {
      console.log('🔄 SimpleLiveOverlay: Initial order data fetch');
      this._scanForNewFormulas();
      this._fetchInitialPrices();
    }, 100); // Almost immediate
    
    // Real-time retry for non-price data - check every 2 seconds like LTP
    setInterval(() => {
      const loadingOverlays: string[] = [];

      // Check for overlays still showing "Loading..."
      this._overlayMap.forEach((key, overlay) => {
        const valueDisplay = overlay.querySelector('.live-price-value') as HTMLElement;
        if (valueDisplay && valueDisplay.textContent === 'Loading...') {
          const field = overlay.getAttribute('data-field') || 'ltp';
          // Retry for order_, trailing_stop_, and position_size fields (not standard price fields)
          const retriableFields = ['order_', 'trailing_stop_price', 'trailing_stop_status', 'tsl_order_status', 'position_size'];
          const shouldRetry = retriableFields.some(prefix => field.startsWith(prefix) || field === prefix);
          if (shouldRetry) {
            const [symbol] = key.split('-');
            loadingOverlays.push(`${symbol}:${field}`);
          }
        }
      });

      if (loadingOverlays.length > 0) {
        console.log(`🔄 Real-time retry: ${loadingOverlays.length} overlays still loading: ${loadingOverlays.join(', ')}`);
        // Fetch data in real-time, but DON'T rescan formulas to prevent oscillation
          this._fetchInitialPrices();
        }
    }, 2000); // Check every 2 seconds - real-time like LTP
  }


}

// Auto-instantiate the overlay when this module is imported for instant startup
if (typeof window !== 'undefined' && !((window as any)._simpleLiveOverlay)) {
  const ensure = () => {
    const gApp = (window as any).gristApp;
    if (gApp && gApp.comm) {
      // Immediate instantiation for maximum speed
      (window as any)._simpleLiveOverlay = new SimpleLiveOverlay();
      console.log('🚀 SimpleLiveOverlay auto-instantiated instantly');
    } else {
      // Retry quickly if not ready
      setTimeout(ensure, 10);
    }
  };
  
  // Start immediately or on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensure);
  } else {
    ensure();
  }
}

